import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Paper,
  Stack,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Divider,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  FormControlLabel,
  Switch,
  Radio,
  RadioGroup,
} from '@mui/material';
import {
  simulateCameraLayout,
  fetchCameraLayout,
  saveCameraLayout,
  CameraSpec,
  LayoutSimResult,
  MountType,
  MOUNT_DEFAULTS,
  DEFAULT_POOL_LENGTH_M,
  DEFAULT_POOL_WIDTH_M,
  DEFAULT_LANE_COUNT,
} from '../../services/cameraLayoutService';
import DraftNumberField from '../common/DraftNumberField';
import {
  applyCameraOptics,
  DEFAULT_FOCAL_LENGTH_MM,
  DEFAULT_FOV_H_DEG,
  DEFAULT_FOV_V_DEG,
  DEFAULT_RESOLUTION_H,
  DEFAULT_RESOLUTION_W,
  layoutDefaultOptics,
  RESOLUTION_PRESETS,
} from '../../utils/cameraOptics';
import { seedWallPoolEdgeOffsets, type CameraSpecSeed } from '../../utils/cameraSpecWizard';
import {
  buildCamerasFromGrid,
  clampWallPoolEdgeOffsets,
  DEFAULT_WALL_MOUNT_OFFSET_M,
  DEFAULT_WALL_POOL_EDGE_OFFSET_M,
  DEFAULT_WALL_POOL_EDGE_OFFSETS_M,
  alongEdgeMeters,
  absoluteToCameraPanDisplayDeg,
  cameraPanDisplayToAbsoluteDeg,
  inferWallSide,
  isWallCameraSpec,
  normFromAlongEdgeMeters,
  poolBoundsFromPolygon,
  wallCameraToPoolGapM,
  wallFaceHeadingDeg,
  wallPoolEdgeForSide,
  formatMeters1,
  type PoolBounds,
  type WallPoolEdgeOffsetsM,
  type WallSide,
} from '../../utils/cameraGridPlacement';
import {
  canvasToNormInBounds,
  computeFacilityCanvasLayout,
  drawHorizontalDimension,
  drawVerticalDimension,
  defaultPoolPolygonRect,
  expandPoolBoundsByDeckMeters,
  getPoolViewport,
  isNormInsidePoolBounds,
  normToCanvasInBounds,
  poolPolygonBounds,
} from '../../utils/poolLayoutCanvas';
import {
  aimPoseAfterPosition,
  autoOptimizeCameraSettings,
  headingDegAfterPositionMove,
  setWallCoverageProfile,
  WALL_TILT_RECOMMENDED_MAX,
  WALL_TILT_RECOMMENDED_MIN,
} from '../../utils/cameraAutoOptimize';
import {
  downloadLayoutMapPng,
  downloadLayoutReportJson,
  printLayoutReport,
  type CameraLayoutReportInput,
} from '../../utils/cameraLayoutExport';
import {
  footprintHandlesOnCanvas,
  hitFootprintHandleCanvas,
  drawHandleDragAxis,
  horizontalFovDegFromPoint,
  normHeadingDegFromPoint,
  tiltDegFromGroundPoint,
  previewFootprintPolygonNorm,
  cameraMarkerNorm,
  cameraStoredNormFromMarker,
  type CanvasHandleHit,
  type FootprintHandleCanvas,
} from '../../utils/cameraFootprintHandles';
import api from '../../services/api';

const CANVAS_HANDLE_TIPS: Record<CanvasHandleHit, string> = {
  camera:
    '카메라 설치 위치. 드래그하면 그 벽 위에서 이동하고, 팬은 그 자리 앞 수면을 보도록 다시 계산됨. 팬을 직접 바꾼 카메라는 팬을 유지.',
  heading:
    '팬(좌우 회전). 실제 CCTV 팬과 같음. 드래그하는 동안 시야 범위가 도면에 표시됨.',
  tilt:
    '틸트(상하 각도). 실제 CCTV 틸트와 같음. 드래그하면 주황 바닥 시야가 가깝게/멀게 변.',
  focal:
    '줌(초점거리). 실제 렌즈 줌과 같음. 넓히면 광각·좁히면 망원. H/V 화각은 줌에 따라 자동.',
};

/** 치수선 풀 외곽으로부터 px (벽 배치용) — 풀에서 살짝 바깥 */
const DIM_GAP_ROW = 22;
const DIM_GAP_TOTAL = 34;
const DIM_GAP_WIDTH = 32;
const DIM_GAP_ROW_VERT = 22;
/** 풀 가장자리 ↔ 카메라(벽 쪽) 치수 간격 */
const DIM_GAP_WALL_CAM = 28;
const MIN_DIM_M = 0.25;

const DEFAULT_POOL: number[][] = defaultPoolPolygonRect(0.035);

/** 히트맵 셀 값(겹친 카메라 대수) → 초록 불투명도 */
const heatmapAlphaForStrength = (strength: number) =>
  strength <= 0 ? 0 : Math.min(0.75, 0.1 + strength * 0.14);

export type LayoutHydrateInfo = {
  poolLengthM: number;
  poolWidthM: number;
  mountMode: 'ceiling' | 'wall';
  lensFixed: boolean;
  focalLengthMm: number;
  installHeightM: number;
};

type Props = {
  poolId: number;
  poolName?: string | null;
  /** 시방 마법사에서 전달: token이 바뀔 때마다 격자·높이·자동배치 적용 */
  specSeed?: CameraSpecSeed | null;
  specSeedToken?: number;
  /** 서버 레이아웃 로드·저장 직후 시방 입력 동기화 */
  onLayoutHydrated?: (info: LayoutHydrateInfo) => void;
};

/** 팬·틸트·줌 등 필드 안내 — 마우스 올리면 팝업 */
const FieldTip: React.FC<{ title: React.ReactNode; children: React.ReactElement }> = ({
  title,
  children,
}) => (
  <Tooltip title={title} arrow enterDelay={280} leaveDelay={80} placement="top">
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'flex-start',
        verticalAlign: 'top',
        /* Stack 줄 맞춤: TextField 라벨·헬퍼 유무와 무관하게 상단 정렬 */
        '& .MuiFormControl-root': { mt: 0 },
      }}
    >
      {children}
    </Box>
  </Tooltip>
);

/** 기초 입력 숫자칸 — 1줄만 좁게 (2줄은 MUI 기본 폭) */
const COMPACT_INPUT_SX = { width: 72 };
const COMPACT_INPUT_MD_SX = { width: 86 };
/** 기본 줌 옆 H/V-FOV(자동) — 라벨 안 깨질 정도로만 축소 */
const FOV_AUTO_INPUT_SX = { width: 128 };
/** 천장 격자 — 옅은 청색 */
const CEILING_INPUT_SX = {
  width: 72,
  bgcolor: '#e3f2fd',
  borderRadius: 1,
  '& .MuiOutlinedInput-root': { bgcolor: '#e3f2fd' },
  '& .MuiOutlinedInput-input': { bgcolor: '#e3f2fd' },
};
/** 벽 관련 — 옅은 녹색 */
const WALL_INPUT_SX = {
  width: 72,
  bgcolor: '#e8f5e9',
  borderRadius: 1,
  '& .MuiOutlinedInput-root': { bgcolor: '#e8f5e9' },
  '& .MuiOutlinedInput-input': { bgcolor: '#e8f5e9' },
};

const opticsInputFromPartial = (
  c: Partial<CameraSpec>,
  defaultFocalMm: number,
): Parameters<typeof applyCameraOptics>[0] => {
  const mount: MountType = c.mount_type === 'wall' ? 'wall' : 'ceiling';
  const rawFocal = c.focal_length_mm;
  const hasFocal = rawFocal != null && Number.isFinite(Number(rawFocal)) && Number(rawFocal) > 0;
  return {
    mount_type: mount,
    resolution_w: c.resolution_w ?? DEFAULT_RESOLUTION_W,
    resolution_h: c.resolution_h ?? DEFAULT_RESOLUTION_H,
    sensor_width_mm: c.sensor_width_mm,
    max_range_m: c.max_range_m,
    // 초점 우선 — H/V는 applyCameraOptics에서 초점으로만 파생
    focal_length_mm: hasFocal ? Number(rawFocal) : defaultFocalMm,
  };
};

const normalizeCam = (c: Partial<CameraSpec>, defaultFocalMm = DEFAULT_FOCAL_LENGTH_MM): CameraSpec => {
  const mount: MountType = c.mount_type === 'wall' ? 'wall' : 'ceiling';
  const d = MOUNT_DEFAULTS[mount];
  const optics = applyCameraOptics(opticsInputFromPartial(c, defaultFocalMm));
  return {
    x: Number(c.x ?? 50),
    y: Number(c.y ?? 50),
    heading_deg: Number(c.heading_deg ?? 90),
    tilt_deg: Number(c.tilt_deg ?? d.tilt_deg),
    height_m: Number(c.height_m ?? d.height_m),
    mount_type: mount,
    cctv_id: c.cctv_id,
    name: c.name,
    pool_edge_offset_m: mount === 'wall' ? Number(c.pool_edge_offset_m ?? DEFAULT_WALL_POOL_EDGE_OFFSET_M) : 0,
    wall_mount_offset_m: mount === 'wall' ? Number(c.wall_mount_offset_m ?? DEFAULT_WALL_MOUNT_OFFSET_M) : 0,
    ...optics,
  };
};

const CameraLayoutSimulator: React.FC<Props> = ({
  poolId,
  poolName: poolNameProp,
  specSeed = null,
  specSeedToken = 0,
  onLayoutHydrated,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const canvasHostRef = useRef<HTMLDivElement>(null);
  const [hostWidthPx, setHostWidthPx] = useState(960);
  const [poolName, setPoolName] = useState<string>((poolNameProp || '').trim());
  const [poolPolygon, setPoolPolygon] = useState<number[][]>(DEFAULT_POOL);
  const [poolLengthM, setPoolLengthM] = useState(DEFAULT_POOL_LENGTH_M);
  const [poolWidthM, setPoolWidthM] = useState(DEFAULT_POOL_WIDTH_M);
  const [laneCount, setLaneCount] = useState(DEFAULT_LANE_COUNT);
  const [ceilingGridRows, setCeilingGridRows] = useState(0);
  const [ceilingGridCols, setCeilingGridCols] = useState(0);
  const [wallGridRows, setWallGridRows] = useState(0);
  const [wallGridCols, setWallGridCols] = useState(0);
  /** 왼쪽·오른쪽 짧은 변 벽캠 대수 (행·열과 독립, 0=없음) */
  const [wallLeftCount, setWallLeftCount] = useState(0);
  const [wallRightCount, setWallRightCount] = useState(0);
  /** ON: 모서리 커버용으로 끝단 간격 축소. OFF(기본): 카메라 등간격 */
  const [flexInstallSpacing, setFlexInstallSpacing] = useState(false);
  const [wallPoolEdgeOffsetsM, setWallPoolEdgeOffsetsM] = useState<WallPoolEdgeOffsetsM>({
    ...DEFAULT_WALL_POOL_EDGE_OFFSETS_M,
  });
  const [wallMountOffsetM, setWallMountOffsetM] = useState(DEFAULT_WALL_MOUNT_OFFSET_M);

  useEffect(() => {
    const el = canvasHostRef.current;
    if (!el) return;
    const apply = () => setHostWidthPx(Math.max(480, el.clientWidth || 960));
    apply();
    const ro = new ResizeObserver(apply);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  /** 풀+데크 실측·화면 폭에 맞춰 캔버스 비트맵 크기 가변 */
  const canvasLayout = useMemo(
    () =>
      computeFacilityCanvasLayout(
        poolLengthM,
        poolWidthM,
        wallPoolEdgeOffsetsM,
        hostWidthPx,
      ),
    [poolLengthM, poolWidthM, wallPoolEdgeOffsetsM, hostWidthPx],
  );

  const resolveView = useCallback(() => {
    const c = canvasRef.current;
    const w = c?.width ?? canvasLayout.canvasW;
    const h = c?.height ?? canvasLayout.canvasH;
    const poolBounds = poolPolygonBounds(poolPolygon);
    const viewBounds = expandPoolBoundsByDeckMeters(
      poolBounds,
      poolLengthM,
      poolWidthM,
      wallPoolEdgeOffsetsM,
    );
    const vp = getPoolViewport(
      w,
      h,
      canvasLayout.worldLengthM,
      canvasLayout.worldWidthM,
      20,
      canvasLayout.margins,
    );
    return {
      vp,
      poolBounds,
      viewBounds,
      fitScale: canvasLayout.fitScale,
    };
  }, [poolPolygon, poolLengthM, poolWidthM, wallPoolEdgeOffsetsM, canvasLayout]);

  const setWallPoolEdgeSide = useCallback((side: WallSide, raw: number) => {
    const v = Math.max(0, Number(raw) || 0);
    setWallPoolEdgeOffsetsM((prev) => ({ ...prev, [side]: v }));
  }, []);
  const [defaultFocalLengthMm, setDefaultFocalLengthMm] = useState(DEFAULT_FOCAL_LENGTH_MM);
  /** true=고정 렌즈(자동 배치가 초점·H/V 안 바꿈), false=가변 줌 */
  const [lensFixed, setLensFixed] = useState(true);
  const [defaultFovHDeg, setDefaultFovHDeg] = useState(DEFAULT_FOV_H_DEG);
  const [defaultFovVDeg, setDefaultFovVDeg] = useState(DEFAULT_FOV_V_DEG);
  const [installHeightM, setInstallHeightM] = useState(4.5);
  const [resolutionW, setResolutionW] = useState(DEFAULT_RESOLUTION_W);
  const [resolutionH, setResolutionH] = useState(DEFAULT_RESOLUTION_H);
  const [cameras, setCameras] = useState<CameraSpec[]>([]);
  const [selected, setSelected] = useState<number | null>(null);
  const [dragging, setDragging] = useState(false);
  const [canvasDrag, setCanvasDrag] = useState<CanvasHandleHit | null>(null);
  const [addMode, setAddMode] = useState(false);
  const [addMount, setAddMount] = useState<MountType>('ceiling');
  const [result, setResult] = useState<LayoutSimResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [simulating, setSimulating] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const hoverTipRef = useRef<string | null>(null);
  const hoverTipElRef = useRef<HTMLDivElement | null>(null);
  const visibleHandlesRef = useRef<FootprintHandleCanvas[]>([]);
  const hoverHandleIdRef = useRef<CanvasHandleHit | null>(null);
  const simTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isHydratingRef = useRef(false);
  const loadGenerationRef = useRef(0);
  const manualFocalLocksRef = useRef<Set<number>>(new Set());
  const manualDerivedRef = useRef<
    Map<
      number,
      Partial<
        Pick<
          CameraSpec,
          'tilt_deg' | 'heading_deg' | 'focal_length_mm' | 'fov_h_deg' | 'fov_v_deg' | 'max_range_m'
        >
      >
    >
  >(new Map());
  const manualOpticsLockRef = useRef<Set<number>>(new Set());
  const camerasRef = useRef<CameraSpec[]>([]);
  camerasRef.current = cameras;
  /** 시방 시드 적용 세대 — loadLayout이 나중에 덮어쓰지 않도록 */
  const specSeedAppliedTokenRef = useRef(0);
  const specSeedRef = useRef(specSeed);
  specSeedRef.current = specSeed;
  const selectedRef = useRef<number | null>(selected);
  selectedRef.current = selected;

  const isOpticsLocked = useCallback(
    (index: number) =>
      manualFocalLocksRef.current.has(index) || manualOpticsLockRef.current.has(index),
    [],
  );

  const syncDefaultOpticsFromFocal = useCallback(
    (focalMm: number, rw: number, rh: number) => {
      const o = layoutDefaultOptics(focalMm, rw, rh);
      setDefaultFovHDeg(o.fov_h_deg);
      setDefaultFovVDeg(o.fov_v_deg);
    },
    [],
  );

  useEffect(() => {
    const fromProp = (poolNameProp || '').trim();
    if (fromProp) {
      setPoolName(fromProp);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const res = await api.get<any>(`/api/pools/${poolId}`);
        const raw = res?.data ?? res;
        const name = String(raw?.name || raw?.pool_name || '').trim();
        if (!cancelled && name) setPoolName(name);
      } catch {
        /* 이름 없으면 ID만 사용 */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [poolId, poolNameProp]);

  const runSim = useCallback(
    async (cams: CameraSpec[], poly: number[][], lenM: number, widM: number) => {
      if (!cams.length) {
        setResult(null);
        return;
      }
      setSimulating(true);
      setErr(null);
      try {
        const res = await simulateCameraLayout(poolId, poly, cams, {
          pool_length_m: lenM,
          pool_width_m: widM,
        });
        setResult(res);
      } catch (e: any) {
        setResult(null);
        setErr(
          e?.response?.data?.detail ||
            e?.message ||
            '커버리지 계산 실패 — 백엔드 재시작 후 다시 시도.',
        );
      } finally {
        setSimulating(false);
      }
    },
    [poolId],
  );

  const scheduleSim = useCallback(
    (cams: CameraSpec[], poly: number[][], lenM: number, widM: number, debounceMs = 400) => {
      if (simTimer.current) clearTimeout(simTimer.current);
      simTimer.current = setTimeout(() => {
        void runSim(cams, poly, lenM, widM);
      }, debounceMs);
    },
    [runSim],
  );

  const applyFovAntiOverlap = useCallback(
    (
      cams: CameraSpec[],
      lenM: number,
      widM: number,
      bounds: PoolBounds,
      opts?: {
        fillCoverage?: boolean;
        ceilingRows?: number;
        ceilingCols?: number;
        wallRows?: number;
        wallCols?: number;
        flexInstallSpacing?: boolean;
      },
    ) => {
      const cRows = opts?.ceilingRows ?? ceilingGridRows;
      const cCols = opts?.ceilingCols ?? ceilingGridCols;
      const wRows = opts?.wallRows ?? wallGridRows;
      const wCols = opts?.wallCols ?? wallGridCols;
      const flex =
        opts?.flexInstallSpacing ??
        (opts?.fillCoverage ? flexInstallSpacing : false);
      return autoOptimizeCameraSettings(cams, lenM, widM, {
        // fillCoverage: 가변은 유통 기본 초점부터. 고정은 사용자 mm 유지(아래 lock).
        defaultFocalMm: lensFixed
          ? defaultFocalLengthMm
          : opts?.fillCoverage
            ? DEFAULT_FOCAL_LENGTH_MM
            : defaultFocalLengthMm,
        defaultFovHDeg,
        defaultFovVDeg,
        fillCoverage: opts?.fillCoverage,
        flexInstallSpacing: !!opts?.fillCoverage && flex,
        lockedFocalIndices: new Set(
          lensFixed
            ? cams.map((_, i) => i)
            : [...manualFocalLocksRef.current, ...manualOpticsLockRef.current],
        ),
        ceilingGrid:
          cRows > 0 && cCols > 0 ? { rows: cRows, cols: cCols, bounds } : undefined,
        wallGrid:
          wRows > 0 && wCols > 0 ? { rows: wRows, cols: wCols, bounds } : undefined,
      }).map((c, i) => {
        if (lensFixed || isOpticsLocked(i)) {
          const focal =
            lensFixed
              ? defaultFocalLengthMm
              : Number(cams[i]?.focal_length_mm) ||
                Number(c.focal_length_mm) ||
                defaultFocalLengthMm;
          const base = lensFixed ? { ...cams[i], ...c, focal_length_mm: focal } : { ...cams[i], ...c };
          return normalizeCam(base, focal);
        }
        if (opts?.fillCoverage) {
          const focal = Number(c.focal_length_mm) || defaultFocalLengthMm;
          return normalizeCam(
            {
              ...c,
              resolution_w: resolutionW,
              resolution_h: resolutionH,
              focal_length_mm: focal,
            },
            focal,
          );
        }
        return normalizeCam(
          {
            ...c,
            focal_length_mm: defaultFocalLengthMm,
            resolution_w: resolutionW,
            resolution_h: resolutionH,
          },
          defaultFocalLengthMm,
        );
      });
    },
    [
      defaultFocalLengthMm,
      defaultFovHDeg,
      defaultFovVDeg,
      resolutionW,
      resolutionH,
      isOpticsLocked,
      lensFixed,
      ceilingGridRows,
      ceilingGridCols,
      wallGridRows,
      wallGridCols,
      flexInstallSpacing,
    ],
  );

  const applyBaseToCameras = useCallback(
    (cams: CameraSpec[], keepAutoOptics?: boolean) => {
      const bounds = poolBoundsFromPolygon(poolPolygon);
      return cams.map((c, i) => {
        const side = inferWallSide(c, bounds);
        const base: Partial<CameraSpec> = {
          ...c,
          height_m: installHeightM,
          resolution_w: resolutionW,
          resolution_h: resolutionH,
          pool_edge_offset_m:
            c.mount_type === 'wall'
              ? wallPoolEdgeForSide(wallPoolEdgeOffsetsM, side)
              : c.pool_edge_offset_m,
          wall_mount_offset_m:
            c.mount_type === 'wall' ? wallMountOffsetM : c.wall_mount_offset_m,
        };
        if (isOpticsLocked(i)) {
          const focal = Number(c.focal_length_mm) || defaultFocalLengthMm;
          return normalizeCam({ ...base, focal_length_mm: focal }, focal);
        }
        if (keepAutoOptics) {
          const focal = Number(c.focal_length_mm) || defaultFocalLengthMm;
          return normalizeCam({ ...base, focal_length_mm: focal }, focal);
        }
        return normalizeCam(
          {
            ...base,
            focal_length_mm: defaultFocalLengthMm,
          },
          defaultFocalLengthMm,
        );
      });
    },
    [
      defaultFocalLengthMm,
      defaultFovHDeg,
      defaultFovVDeg,
      isOpticsLocked,
      installHeightM,
      resolutionW,
      resolutionH,
      wallPoolEdgeOffsetsM,
      wallMountOffsetM,
      poolPolygon,
    ],
  );

  const applyDerivedOverrides = useCallback(
    (cams: CameraSpec[]) =>
      cams.map((c, i) => {
        const o = manualDerivedRef.current.get(i);
        if (!o) return c;
        let merged: Partial<CameraSpec> = { ...c, ...o };
        if (o.fov_h_deg != null || o.fov_v_deg != null) {
          // 구버전 수동 FOV → 초점으로 환산 후 저장
          const oOptics = applyCameraOptics({
            mount_type: c.mount_type,
            ...(o.fov_h_deg != null ? { fov_h_deg: Number(o.fov_h_deg) } : {}),
            ...(o.fov_v_deg != null ? { fov_v_deg: Number(o.fov_v_deg) } : {}),
            resolution_w: c.resolution_w,
            resolution_h: c.resolution_h,
            max_range_m: o.max_range_m ?? c.max_range_m,
          });
          merged = {
            ...merged,
            focal_length_mm: oOptics.focal_length_mm,
            max_range_m: o.max_range_m ?? c.max_range_m,
          };
          delete merged.fov_h_deg;
          delete merged.fov_v_deg;
        } else if (o.max_range_m != null) {
          merged = { ...merged, max_range_m: o.max_range_m };
        }
        if (o.focal_length_mm != null) {
          delete merged.fov_h_deg;
          delete merged.fov_v_deg;
          merged = { ...merged, focal_length_mm: o.focal_length_mm };
        }
        const focal = Number(merged.focal_length_mm) || defaultFocalLengthMm;
        return normalizeCam(merged, focal);
      }),
    [defaultFocalLengthMm],
  );

  const recomputeDerived = useCallback(
    (
      cams: CameraSpec[],
      opts?: {
        fillCoverage?: boolean;
        poolPolygon?: number[][];
        poolLengthM?: number;
        poolWidthM?: number;
        installHeightM?: number;
        wallPoolEdgeOffsetM?: number;
        wallPoolEdgeOffsetsM?: WallPoolEdgeOffsetsM;
        wallMountOffsetM?: number;
        ceilingRows?: number;
        ceilingCols?: number;
        wallRows?: number;
        wallCols?: number;
        flexInstallSpacing?: boolean;
      },
    ) => {
      if (!cams.length) return cams;
      const poly = opts?.poolPolygon ?? poolPolygon;
      const lenM = opts?.poolLengthM ?? poolLengthM;
      const widM = opts?.poolWidthM ?? poolWidthM;
      const height = opts?.installHeightM ?? installHeightM;
      const edgeOffsets = clampWallPoolEdgeOffsets(
        opts?.wallPoolEdgeOffsetsM ?? wallPoolEdgeOffsetsM,
        opts?.wallPoolEdgeOffsetM ?? DEFAULT_WALL_POOL_EDGE_OFFSET_M,
      );
      const mountM = opts?.wallMountOffsetM ?? wallMountOffsetM;
      const bounds = poolBoundsFromPolygon(poly);
      const fill = !!opts?.fillCoverage;
      // 시드 첫 적용 시 state가 아직 옛값일 수 있어 opts 높이·오프셋 우선
      const seeded = cams.map((c, i) => {
        const side = inferWallSide(c, bounds);
        const base: Partial<CameraSpec> = {
          ...c,
          height_m: height,
          resolution_w: resolutionW,
          resolution_h: resolutionH,
          pool_edge_offset_m:
            c.mount_type === 'wall'
              ? wallPoolEdgeForSide(edgeOffsets, side)
              : c.pool_edge_offset_m,
          wall_mount_offset_m:
            c.mount_type === 'wall' ? mountM : c.wall_mount_offset_m,
        };
        if (isOpticsLocked(i)) {
          const f = Number(c.focal_length_mm) || defaultFocalLengthMm;
          return normalizeCam({ ...base, focal_length_mm: f }, f);
        }
        if (fill) {
          const f = Number(c.focal_length_mm) || defaultFocalLengthMm;
          return normalizeCam({ ...base, focal_length_mm: f }, f);
        }
        return normalizeCam(
          { ...base, focal_length_mm: defaultFocalLengthMm },
          defaultFocalLengthMm,
        );
      });
      const optimized = applyFovAntiOverlap(seeded, lenM, widM, bounds, opts);
      return applyDerivedOverrides(optimized);
    },
    [
      applyFovAntiOverlap,
      applyDerivedOverrides,
      poolPolygon,
      poolLengthM,
      poolWidthM,
      installHeightM,
      wallPoolEdgeOffsetsM,
      wallMountOffsetM,
      resolutionW,
      resolutionH,
      defaultFocalLengthMm,
      isOpticsLocked,
    ],
  );

  const recomputeDerivedRef = useRef(recomputeDerived);
  recomputeDerivedRef.current = recomputeDerived;
  const scheduleSimRef = useRef(scheduleSim);
  scheduleSimRef.current = scheduleSim;
  /** API 로드 직후 기초입력 일괄 반영 시 재계산 effect 1회 스킵 (저장값 유지·깜빡임 방지) */
  const suppressAutoRecomputeRef = useRef(false);

  const commitCameras = useCallback(
    (next: CameraSpec[], sel: number | null = selected) => {
      setCameras(next);
      setSelected(sel);
      scheduleSim(next, poolPolygon, poolLengthM, poolWidthM);
    },
    [selected, poolPolygon, poolLengthM, poolWidthM, scheduleSim],
  );

  const applyAutoPlacement = useCallback(
    (poly: number[][], lenM: number, widM: number) => {
      setWallCoverageProfile('balanced_20260722');
      const bounds = poolBoundsFromPolygon(poly);
      const partials = buildCamerasFromGrid({
        bounds,
        ceilingRows: ceilingGridRows,
        ceilingCols: ceilingGridCols,
        wallRows: wallGridRows,
        wallCols: wallGridCols,
        wallLeftCount,
        wallRightCount,
        wallPoolEdgeOffsetsM,
        wallMountOffsetM,
        defaultFocalLengthMm: DEFAULT_FOCAL_LENGTH_MM,
      });
      if (!partials.length) {
        setCameras([]);
        setSelected(null);
        setResult(null);
        setMsg('배치 0 — 천장·벽 행/열·좌벽·우벽이 모두 0이면 카메라를 두지 않음.');
        return;
      }
      // 기본 초점으로 리셋 후 1회 최적화 — 반복 클릭해도 동일 결과
      const baseFocal = Math.max(1.8, Math.min(16, defaultFocalLengthMm || DEFAULT_FOCAL_LENGTH_MM));
      setDefaultFocalLengthMm(baseFocal);
      syncDefaultOpticsFromFocal(baseFocal, resolutionW, resolutionH);
      const placed = partials.map((p) =>
        normalizeCam(
          {
            ...p,
            height_m: installHeightM,
            focal_length_mm: baseFocal,
            resolution_w: resolutionW,
            resolution_h: resolutionH,
          },
          baseFocal,
        ),
      );
      manualFocalLocksRef.current = new Set();
      manualOpticsLockRef.current = new Set();
      manualDerivedRef.current = new Map();
      if (lensFixed) {
        placed.forEach((_, i) => manualFocalLocksRef.current.add(i));
      }
      suppressAutoRecomputeRef.current = true;
      const adjusted = recomputeDerived(placed, {
        fillCoverage: true,
        poolPolygon: poly,
        poolLengthM: lenM,
        poolWidthM: widM,
        installHeightM,
        wallPoolEdgeOffsetsM,
        wallMountOffsetM,
        ceilingRows: ceilingGridRows,
        ceilingCols: ceilingGridCols,
        wallRows: wallGridRows,
        wallCols: wallGridCols,
        flexInstallSpacing,
      });
      // 고정 렌즈면 최적화 후에도 초점·H/V를 지정 mm으로 강제 재동기화
      const forced = lensFixed
        ? adjusted.map((c) =>
            normalizeCam(
              {
                ...c,
                height_m: installHeightM,
                focal_length_mm: baseFocal,
                resolution_w: resolutionW,
                resolution_h: resolutionH,
              },
              baseFocal,
            ),
          )
        : adjusted;
      if (lensFixed) {
        manualFocalLocksRef.current = new Set(forced.map((_, i) => i));
      }
      commitCameras(forced, forced.length ? 0 : null);
      const nCeil = forced.filter((c) => c.mount_type === 'ceiling').length;
      const nWall = forced.filter((c) => c.mount_type === 'wall').length;
      setMsg(
        flexInstallSpacing
          ? `자동 배치(유동 ON·렌즈 ${lensFixed ? '고정' : '가변'} ${baseFocal} mm): 총 ${forced.length}대 (천장 ${nCeil} / 벽 ${nWall}).`
          : `자동 배치(등간격·렌즈 ${lensFixed ? '고정' : '가변'} ${baseFocal} mm): 총 ${forced.length}대 (천장 ${nCeil} / 벽 ${nWall}).`,
      );
    },
    [
      ceilingGridRows,
      ceilingGridCols,
      wallGridRows,
      wallGridCols,
      wallLeftCount,
      wallRightCount,
      wallPoolEdgeOffsetsM,
      wallMountOffsetM,
      defaultFovHDeg,
      defaultFovVDeg,
      installHeightM,
      resolutionW,
      resolutionH,
      recomputeDerived,
      commitCameras,
      flexInstallSpacing,
      syncDefaultOpticsFromFocal,
      lensFixed,
      defaultFocalLengthMm,
    ],
  );

  const applyFromSpecSeed = useCallback(
    (seed: CameraSpecSeed) => {
      const len = Math.max(1, seed.poolLengthM);
      const wid = Math.max(1, seed.poolWidthM);
      const h = Math.max(2, seed.installHeightM);
      const focal = Math.max(
        1.8,
        Math.min(16, seed.focalLengthMm || DEFAULT_FOCAL_LENGTH_MM),
      );
      const lensIsFixed = seed.lensMode !== 'variable';
      const rw = seed.resolutionW || DEFAULT_RESOLUTION_W;
      const rh = seed.resolutionH || DEFAULT_RESOLUTION_H;
      const poly = defaultPoolPolygonRect(0.035);

      // 격자·치수 setState로 인한 이중 재계산 1회 스킵 (아래에서 즉시 시뮬)
      suppressAutoRecomputeRef.current = true;
      setPoolLengthM(len);
      setPoolWidthM(wid);
      setPoolPolygon(poly);
      setInstallHeightM(h);
      setCeilingGridRows(seed.mountMode === 'ceiling' ? seed.ceilingRows : 0);
      setCeilingGridCols(seed.mountMode === 'ceiling' ? seed.ceilingCols : 0);
      setWallGridRows(seed.mountMode === 'wall' ? seed.wallRows : 0);
      setWallGridCols(seed.mountMode === 'wall' ? seed.wallCols : 0);
      setWallLeftCount(seed.mountMode === 'wall' ? seed.wallLeftCount : 0);
      setWallRightCount(seed.mountMode === 'wall' ? seed.wallRightCount : 0);
      const seedEdges = seedWallPoolEdgeOffsets(seed);
      setWallPoolEdgeOffsetsM(seedEdges);
      setWallMountOffsetM(seed.wallMountOffsetM);
      setLensFixed(lensIsFixed);
      setDefaultFocalLengthMm(focal);
      setResolutionW(rw);
      setResolutionH(rh);
      syncDefaultOpticsFromFocal(focal, rw, rh);

      const ceilR = seed.mountMode === 'ceiling' ? seed.ceilingRows : 0;
      const ceilC = seed.mountMode === 'ceiling' ? seed.ceilingCols : 0;
      const wR = seed.mountMode === 'wall' ? seed.wallRows : 0;
      const wC = seed.mountMode === 'wall' ? seed.wallCols : 0;
      const wL = seed.mountMode === 'wall' ? seed.wallLeftCount : 0;
      const wRi = seed.mountMode === 'wall' ? seed.wallRightCount : 0;

      const bounds = poolBoundsFromPolygon(poly);
      const partials = buildCamerasFromGrid({
        bounds,
        ceilingRows: ceilR,
        ceilingCols: ceilC,
        wallRows: wR,
        wallCols: wC,
        wallLeftCount: wL,
        wallRightCount: wRi,
        wallPoolEdgeOffsetsM: seedEdges,
        wallMountOffsetM: seed.wallMountOffsetM,
        defaultFocalLengthMm: focal,
      });
      if (!partials.length) {
        setCameras([]);
        setSelected(null);
        setResult(null);
        setMsg('시방 시드 적용 — 배치 0대 (격자 확인).');
        return;
      }
      const placed = partials.map((p) =>
        normalizeCam(
          {
            ...p,
            height_m: h,
            focal_length_mm: focal,
            resolution_w: rw,
            resolution_h: rh,
            tilt_deg:
              (p.mount_type === 'wall' ? seed.wallTiltDeg : seed.ceilingTiltDeg) ||
              (p.mount_type === 'wall' ? 20 : 90),
          },
          focal,
        ),
      );
      manualFocalLocksRef.current = new Set();
      manualOpticsLockRef.current = new Set();
      manualDerivedRef.current = new Map();
      if (lensIsFixed) {
        placed.forEach((_, i) => manualFocalLocksRef.current.add(i));
      }
      const adjusted = recomputeDerived(placed, {
        fillCoverage: true,
        poolPolygon: poly,
        poolLengthM: len,
        poolWidthM: wid,
        installHeightM: h,
        wallPoolEdgeOffsetsM: seedEdges,
        wallMountOffsetM: seed.wallMountOffsetM,
        ceilingRows: ceilR,
        ceilingCols: ceilC,
        wallRows: wR,
        wallCols: wC,
        // 시방 적용도 화면 스위치 따름 — 기본 등간격
        flexInstallSpacing,
      });
      const forced = lensIsFixed
        ? adjusted.map((c) =>
            normalizeCam(
              {
                ...c,
                height_m: h,
                focal_length_mm: focal,
                resolution_w: rw,
                resolution_h: rh,
              },
              focal,
            ),
          )
        : adjusted;
      if (lensIsFixed) {
        manualFocalLocksRef.current = new Set(forced.map((_, i) => i));
      }
      setCameras(forced);
      const keepSel =
        selectedRef.current != null &&
        selectedRef.current >= 0 &&
        selectedRef.current < forced.length
          ? selectedRef.current
          : forced.length
            ? 0
            : null;
      setSelected(keepSel);
      scheduleSim(forced, poly, len, wid, 0);
      const nCeil = forced.filter((c) => c.mount_type === 'ceiling').length;
      const nWall = forced.filter((c) => c.mount_type === 'wall').length;
      setMsg(
        `시방 적용: ${len}×${wid} m, H=${h} m, 렌즈 ${lensIsFixed ? '고정' : '가변'} ${focal} mm → 총 ${forced.length}대 (천장 ${nCeil}/벽 ${nWall}).`,
      );
      onLayoutHydrated?.({
        poolLengthM: len,
        poolWidthM: wid,
        mountMode: seed.mountMode === 'wall' ? 'wall' : 'ceiling',
        lensFixed: lensIsFixed,
        focalLengthMm: focal,
        installHeightM: h,
      });
      requestAnimationFrame(() => {
        canvasRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      });
    },
    [
      syncDefaultOpticsFromFocal,
      recomputeDerived,
      scheduleSim,
      onLayoutHydrated,
      flexInstallSpacing,
    ],
  );

  // token 변경 시에만 시드 적용 — applyFromSpecSeed 항등성 변화로 재실행되면 선택이 0으로 복귀함
  useEffect(() => {
    if (!specSeedToken) return;
    const seed = specSeedRef.current;
    if (!seed) return;
    specSeedAppliedTokenRef.current = specSeedToken;
    applyFromSpecSeed(seed);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- token만
  }, [specSeedToken]);

  const applyWallLayoutPreset = useCallback(
    (preset: '1' | 'baseline') => {
      if (preset === '1') {
        setWallCoverageProfile('preset_1_20260722');
      } else {
        setWallCoverageProfile('baseline_20260722');
      }
      const label = preset === '1' ? '1번 셋' : '2번 셋(baseline)';
      if (!camerasRef.current.length) {
        setMsg(`벽 프로파일 「${label}」 — 카메라 없음. 「자동 배치」를 실행`);
        return;
      }
      manualFocalLocksRef.current = new Set();
      manualOpticsLockRef.current = new Set();
      manualDerivedRef.current = new Map();
      const next = recomputeDerived(camerasRef.current, { fillCoverage: true });
      commitCameras(next, selected);
      setMsg(
        preset === '1'
          ? `「1번 셋」 프로파일 적용 — 2026-07-22 저장 스냅샷. 커버리지 칩을 확인.`
          : `「2번 셋」 적용 — baseline(기본 화각·넓은 담당). 「1번 셋」과 겹침·사각을 비교.`,
      );
    },
    [recomputeDerived, commitCameras, selected],
  );

  useEffect(() => {
    if (isHydratingRef.current) return;
    if (suppressAutoRecomputeRef.current) {
      suppressAutoRecomputeRef.current = false;
      return;
    }
    if (!camerasRef.current.length) return;
    const fill =
      (ceilingGridRows > 0 && ceilingGridCols > 0) ||
      (wallGridRows > 0 && wallGridCols > 0) ||
      wallLeftCount > 0 ||
      wallRightCount > 0;
    const next = recomputeDerived(camerasRef.current, { fillCoverage: fill });
    setCameras(next);
    scheduleSim(next, poolPolygon, poolLengthM, poolWidthM);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 기초 입력 변경 시 일괄 재계산
  }, [
    installHeightM,
    poolLengthM,
    poolWidthM,
    laneCount,
    ceilingGridRows,
    ceilingGridCols,
    wallGridRows,
    wallGridCols,
    wallLeftCount,
    wallRightCount,
    wallPoolEdgeOffsetsM,
    wallMountOffsetM,
    defaultFocalLengthMm,
    defaultFovHDeg,
    defaultFovVDeg,
    resolutionW,
    resolutionH,
    recomputeDerived,
    scheduleSim,
    poolPolygon,
  ]);

  const updateCamDerivedField = useCallback(
    (
      index: number,
      field:
        | 'tilt_deg'
        | 'heading_deg'
        | 'focal_length_mm'
        | 'fov_h_deg'
        | 'fov_v_deg'
        | 'max_range_m',
      value: number,
    ) => {
      if (field === 'focal_length_mm' || field === 'fov_h_deg' || field === 'fov_v_deg') {
        manualFocalLocksRef.current.add(index);
        manualOpticsLockRef.current.add(index);
      }
      if (field === 'max_range_m') {
        manualOpticsLockRef.current.add(index);
      }
      const cam = cameras[index];
      if (!cam) return;

      // H/V 입력은 초점(mm)으로만 환산·저장 (초점 우선)
      if (field === 'fov_h_deg' || field === 'fov_v_deg') {
        const o = applyCameraOptics({
          mount_type: cam.mount_type,
          ...(field === 'fov_h_deg' ? { fov_h_deg: value } : { fov_v_deg: value }),
          resolution_w: cam.resolution_w ?? resolutionW,
          resolution_h: cam.resolution_h ?? resolutionH,
        });
        manualDerivedRef.current.set(index, {
          ...manualDerivedRef.current.get(index),
          focal_length_mm: o.focal_length_mm,
        });
        const next = cameras.map((c, i) =>
          i === index
            ? normalizeCam({ ...c, focal_length_mm: o.focal_length_mm }, o.focal_length_mm)
            : c,
        );
        commitCameras(next, selected);
        return;
      }

      manualDerivedRef.current.set(index, {
        ...manualDerivedRef.current.get(index),
        [field]: value,
      });
      let merged: Partial<CameraSpec> = { ...cam, [field]: value };
      if (field === 'focal_length_mm') {
        delete merged.fov_h_deg;
        delete merged.fov_v_deg;
      }
      const next = cameras.map((c, i) =>
        i === index ? normalizeCam(merged, defaultFocalLengthMm) : c,
      );
      commitCameras(next, selected);
    },
    [cameras, commitCameras, selected, defaultFocalLengthMm, resolutionW, resolutionH],
  );

  const buildReportInput = useCallback((): CameraLayoutReportInput => {
    return {
      poolId,
      poolName: poolName || undefined,
      poolPolygon,
      poolLengthM,
      poolWidthM,
      laneCount,
      installHeightM,
      ceilingGridRows,
      ceilingGridCols,
      wallGridRows,
      wallGridCols,
      wallLeftCount,
      wallRightCount,
      wallPoolEdgeOffsetM: wallPoolEdgeOffsetsM.top,
      wallPoolEdgeOffsetsM,
      wallMountOffsetM,
      defaultFocalLengthMm,
      defaultFovHDeg,
      defaultFovVDeg,
      resolutionW,
      resolutionH,
      cameras,
      result,
      canvas: canvasRef.current,
    };
  }, [
    poolId,
    poolName,
    poolLengthM,
    poolWidthM,
    laneCount,
    installHeightM,
    ceilingGridRows,
    ceilingGridCols,
    wallGridRows,
    wallGridCols,
    wallLeftCount,
    wallRightCount,
    wallPoolEdgeOffsetsM,
    wallMountOffsetM,
    defaultFocalLengthMm,
    defaultFovHDeg,
    defaultFovVDeg,
    resolutionW,
    resolutionH,
    cameras,
    result,
    poolPolygon,
  ]);

  const handlePrintReport = useCallback(() => {
    try {
      if (!cameras.length) {
        setMsg('출력할 카메라 배치 없음');
        return;
      }
      printLayoutReport(buildReportInput());
      setMsg('인쇄 창을 열음. 「PDF로 저장」도 여기서 선택 가능.');
    } catch (e: any) {
      setErr(e?.message || '인쇄 준비 실패');
    }
  }, [cameras.length, buildReportInput]);

  const handleDownloadPng = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) {
      setErr('도면 캔버스를 찾기 불가');
      return;
    }
    downloadLayoutMapPng(canvas, poolId, poolName);
    setMsg('배치 도면 PNG를 저장함');
  }, [poolId, poolName]);

  const handleDownloadJson = useCallback(() => {
    if (!cameras.length) {
      setMsg('다운로드할 데이터 없음');
      return;
    }
    downloadLayoutReportJson(buildReportInput());
    setMsg('커버리지·CCTV 표가 포함된 JSON 보고서를 저장함.');
  }, [cameras.length, buildReportInput]);

  const loadLayout = useCallback(async () => {
    const loadGen = ++loadGenerationRef.current;
    // 시방 시드가 이미 적용된 뒤에는 서버 로드로 덮지 않음
    if (specSeedAppliedTokenRef.current > 0) {
      setLoading(false);
      return;
    }
    isHydratingRef.current = true;
    suppressAutoRecomputeRef.current = true;
    manualFocalLocksRef.current = new Set();
    manualOpticsLockRef.current = new Set();
    manualDerivedRef.current = new Map();
    setSelected(null);
    setResult(null);
    setErr(null);
    setLoading(true);
    try {
      const data = await fetchCameraLayout(poolId);
      if (loadGen !== loadGenerationRef.current || specSeedAppliedTokenRef.current > 0) {
        // 시드가 선점했으면 hydrate suppress를 건드리지 않음
        if (specSeedAppliedTokenRef.current === 0) {
          suppressAutoRecomputeRef.current = false;
        }
        return;
      }
      const poly = data?.pool_polygon?.length ? data.pool_polygon : DEFAULT_POOL;
      const lenM = Number(data?.pool_length_m) || DEFAULT_POOL_LENGTH_M;
      const widM = Number(data?.pool_width_m) || DEFAULT_POOL_WIDTH_M;
      const lanes = Math.max(1, Math.min(16, Number(data?.lane_count) || DEFAULT_LANE_COUNT));
      // 저장 레이아웃 없음(empty / 구 cctv 기본): 천장·벽 카메라 수(격자) 전부 0
      const noSavedLayout =
        data?.source === 'empty' ||
        data?.source === 'cctv_location_default' ||
        (!Array.isArray(data?.cameras) && data?.ceiling_grid_rows == null);
      const cgRows = noSavedLayout
        ? 0
        : Math.max(0, Math.min(12, Number(data?.ceiling_grid_rows) || 0));
      const cgCols = noSavedLayout
        ? 0
        : Math.max(0, Math.min(12, Number(data?.ceiling_grid_cols) || 0));
      const wgRows = noSavedLayout
        ? 0
        : Math.max(0, Math.min(12, Number(data?.wall_grid_rows) || 0));
      const wgCols = noSavedLayout
        ? 0
        : Math.max(0, Math.min(12, Number(data?.wall_grid_cols) || 0));
      const wLeft = noSavedLayout
        ? 0
        : Math.max(0, Math.min(12, Number(data?.wall_left_count) || 0));
      const wRight = noSavedLayout
        ? 0
        : Math.max(0, Math.min(12, Number(data?.wall_right_count) || 0));
      const wEdge = Number(data?.wall_pool_edge_offset_m);
      const legacyEdge = Number.isFinite(wEdge) ? wEdge : DEFAULT_WALL_POOL_EDGE_OFFSET_M;
      let edgeOffsets = clampWallPoolEdgeOffsets(
        {
          top: (data as { wall_pool_edge_offset_top_m?: number })?.wall_pool_edge_offset_top_m,
          bottom: (data as { wall_pool_edge_offset_bottom_m?: number })
            ?.wall_pool_edge_offset_bottom_m,
          left: (data as { wall_pool_edge_offset_left_m?: number })?.wall_pool_edge_offset_left_m,
          right: (data as { wall_pool_edge_offset_right_m?: number })?.wall_pool_edge_offset_right_m,
        },
        legacyEdge,
      );
      // 좌·우 벽캠이 없는데 시드 기본 1m 데크만 남아 도면 좌우 공백이 커지는 경우 정리
      if (
        wLeft === 0 &&
        wRight === 0 &&
        edgeOffsets.left === DEFAULT_WALL_POOL_EDGE_OFFSET_M &&
        edgeOffsets.right === DEFAULT_WALL_POOL_EDGE_OFFSET_M
      ) {
        edgeOffsets = { ...edgeOffsets, left: 0, right: 0 };
      }
      const wMount = Number(data?.wall_mount_offset_m);
      const mountM = Number.isFinite(wMount) ? wMount : DEFAULT_WALL_MOUNT_OFFSET_M;
      const defFocal = Number(data?.default_focal_length_mm);
      const defaultFocal =
        Number.isFinite(defFocal) && defFocal > 0 ? defFocal : DEFAULT_FOCAL_LENGTH_MM;
      const defH = Number(data?.default_fov_h_deg);
      const defV = Number(data?.default_fov_v_deg);
      const baseOptics = layoutDefaultOptics(defaultFocal);
      const loadedFovH =
        Number.isFinite(defH) && defH > 0 ? defH : baseOptics.fov_h_deg;
      const loadedFovV =
        Number.isFinite(defV) && defV > 0 ? defV : baseOptics.fov_v_deg;
      if (typeof data?.lens_fixed === 'boolean') {
        setLensFixed(data.lens_fixed);
      }

      setCeilingGridRows(cgRows);
      setCeilingGridCols(cgCols);
      setWallGridRows(wgRows);
      setWallGridCols(wgCols);
      setWallLeftCount(wLeft);
      setWallRightCount(wRight);
      setWallPoolEdgeOffsetsM(edgeOffsets);
      setWallMountOffsetM(mountM);
      setDefaultFocalLengthMm(defaultFocal);
      setDefaultFovHDeg(loadedFovH);
      setDefaultFovVDeg(loadedFovV);

      let cams = noSavedLayout
        ? []
        : (data?.cameras || []).map((c) => normalizeCam(c, defaultFocal));
      let didBuild = false;
      const gridHasAny =
        (cgRows > 0 && cgCols > 0) ||
        (wgRows > 0 && wgCols > 0) ||
        wLeft > 0 ||
        wRight > 0;
      if (!cams.length && gridHasAny) {
        didBuild = true;
        const bounds = poolBoundsFromPolygon(poly);
        cams = buildCamerasFromGrid({
          bounds,
          ceilingRows: cgRows,
          ceilingCols: cgCols,
          wallRows: wgRows,
          wallCols: wgCols,
          wallLeftCount: wLeft,
          wallRightCount: wRight,
          wallPoolEdgeOffsetsM: edgeOffsets,
          wallMountOffsetM: mountM,
          defaultFocalLengthMm: defaultFocal,
        }).map((p) => normalizeCam(p, defaultFocal));
      }
      if (cams.length) {
        const h0 = Number(cams[0]?.height_m);
        if (Number.isFinite(h0) && h0 > 0) setInstallHeightM(h0);
        const rw0 = Number(cams[0]?.resolution_w ?? DEFAULT_RESOLUTION_W);
        const rh0 = Number(cams[0]?.resolution_h ?? DEFAULT_RESOLUTION_H);
        if (Number.isFinite(rw0) && rw0 > 0) setResolutionW(rw0);
        if (Number.isFinite(rh0) && rh0 > 0) setResolutionH(rh0);
      }
      // await 중 시방 시드가 적용됐으면 서버 레이아웃으로 덮지 않음
      if (loadGen !== loadGenerationRef.current || specSeedAppliedTokenRef.current > 0) {
        if (specSeedAppliedTokenRef.current === 0) {
          suppressAutoRecomputeRef.current = false;
        }
        return;
      }
      setPoolPolygon(poly);
      setPoolLengthM(lenM);
      setPoolWidthM(widM);
      setLaneCount(lanes);
      setCameras(cams);
      setSelected(cams.length ? 0 : null);
      setAddMode(false);
      const nWall = cams.filter((c) => c.mount_type === 'wall').length;
      const nCeil = cams.filter((c) => c.mount_type === 'ceiling').length;
      const mountMode: 'ceiling' | 'wall' =
        data?.mount_mode === 'wall' || data?.mount_mode === 'ceiling'
          ? data.mount_mode
          : nWall > nCeil || wgRows * wgCols > 0
            ? 'wall'
            : 'ceiling';
      const heightForUi =
        cams.length && Number.isFinite(Number(cams[0]?.height_m)) && Number(cams[0].height_m) > 0
          ? Number(cams[0].height_m)
          : installHeightM;
      onLayoutHydrated?.({
        poolLengthM: lenM,
        poolWidthM: widM,
        mountMode,
        lensFixed: typeof data?.lens_fixed === 'boolean' ? data.lens_fixed : lensFixed,
        focalLengthMm: defaultFocal,
        installHeightM: heightForUi,
      });
      setMsg(
        cams.length
          ? `카메라 ${cams.length}대 (천장 ${nCeil} / 벽 ${nWall}). 노란선=시야(화각).`
          : '카메라 없음. 천장/벽 행·열·좌벽·우벽을 입력한 뒤 「자동 배치」를 사용.',
      );
      if (cams.length && didBuild) {
        // 자동 생성된 카메라는 즉시 화각까지 완성해서 시뮬
        const finalCams = recomputeDerivedRef.current(cams, { fillCoverage: true });
        setCameras(finalCams);
        setSelected(0);
        scheduleSimRef.current(finalCams, poly, lenM, widM, 0);
      } else if (cams.length) {
        scheduleSimRef.current(cams, poly, lenM, widM, 0);
      } else {
        setResult(null);
      }
    } catch (e: any) {
      if (specSeedAppliedTokenRef.current === 0) {
        suppressAutoRecomputeRef.current = false;
      }
      setErr(e?.response?.data?.detail || e?.message || '불러오기 실패');
    } finally {
      setLoading(false);
      isHydratingRef.current = false;
    }
  }, [poolId]);

  useEffect(() => {
    void loadLayout();
    return () => {
      if (simTimer.current) clearTimeout(simTimer.current);
    };
    // poolId 변경 시에만 서버에서 레이아웃 로드 (loadLayout은 recomputeDerived 등과 분리)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [poolId]);

  const draw = useCallback(() => {
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext('2d');
    if (!ctx) return;
    const w = c.width;
    const h = c.height;
    ctx.clearRect(0, 0, w, h);

    const { vp, poolBounds: bounds, viewBounds, fitScale } = resolveView();
    const spanNormX = Math.max(0.01, bounds.maxX - bounds.minX);
    const spanNormY = Math.max(0.01, bounds.maxY - bounds.minY);
    /** 도면 풀 bbox = 입력한 실측 L×W (정규좌표는 bbox 안에서 0~100% 비율) */
    const poolSpanMAlongX = (nxA: number, nxB: number) =>
      (Math.abs(nxB - nxA) / spanNormX) * poolLengthM;
    const poolSpanMAlongY = (nyA: number, nyB: number) =>
      (Math.abs(nyB - nyA) / spanNormY) * poolWidthM;

    /** 매핑은 데크 포함 viewBounds — 벽캠·벽선이 잘리지 않음 */
    const toCanvas = (nx: number, ny: number, ..._rest: unknown[]) =>
      normToCanvasInBounds(nx, ny, vp, viewBounds, fitScale);

    const poolTL = toCanvas(bounds.minX, bounds.minY);
    const poolTR = toCanvas(bounds.maxX, bounds.minY);
    const poolBL = toCanvas(bounds.minX, bounds.maxY);
    const poolBR = toCanvas(bounds.maxX, bounds.maxY);

    // 시설 외곽(벽·풀 여백 포함) — 데크 영역 안내
    const facTL = toCanvas(viewBounds.minX, viewBounds.minY);
    const facBR = toCanvas(viewBounds.maxX, viewBounds.maxY);
    ctx.save();
    ctx.strokeStyle = 'rgba(96, 125, 139, 0.65)';
    ctx.lineWidth = 1.25;
    ctx.setLineDash([5, 4]);
    ctx.strokeRect(facTL.x, facTL.y, facBR.x - facTL.x, facBR.y - facTL.y);
    ctx.setLineDash([]);
    ctx.fillStyle = 'rgba(236, 239, 241, 0.45)';
    ctx.fillRect(facTL.x, facTL.y, facBR.x - facTL.x, facBR.y - facTL.y);
    ctx.restore();

    ctx.strokeStyle = '#0288d1';
    ctx.lineWidth = 2;
    ctx.beginPath();
    poolPolygon.forEach(([px, py], i) => {
      const { x, y } = toCanvas(px, py, vp);
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    });
    ctx.closePath();
    ctx.fillStyle = 'rgba(2, 136, 209, 0.14)';
    ctx.fill();
    ctx.strokeStyle = '#0277bd';
    ctx.lineWidth = 2.5;
    ctx.stroke();

    const drawLaneLines = () => {
      if (laneCount < 2) return;
      ctx.save();
      ctx.strokeStyle = 'rgba(69, 90, 100, 0.85)';
      ctx.lineWidth = 1.25;
      ctx.setLineDash([8, 6]);
      for (let lane = 1; lane < laneCount; lane++) {
        const ny = bounds.minY + (lane / laneCount) * (bounds.maxY - bounds.minY);
        const p1 = toCanvas(bounds.minX, ny, vp);
        const p2 = toCanvas(bounds.maxX, ny, vp);
        ctx.beginPath();
        ctx.moveTo(p1.x, p1.y);
        ctx.lineTo(p2.x, p2.y);
        ctx.stroke();
      }
      ctx.setLineDash([]);
      ctx.restore();
    };

    drawLaneLines();

    const drawCeilingGrid = () => {
      if (ceilingGridRows <= 0 || ceilingGridCols <= 0) return;
      ctx.save();
      ctx.strokeStyle = 'rgba(2, 119, 189, 0.75)';
      ctx.lineWidth = 1.5;
      ctx.setLineDash([6, 4]);
      for (let col = 0; col <= ceilingGridCols; col++) {
        const nx = bounds.minX + (col / ceilingGridCols) * (bounds.maxX - bounds.minX);
        const p1 = toCanvas(nx, bounds.minY, vp);
        const p2 = toCanvas(nx, bounds.maxY, vp);
        ctx.beginPath();
        ctx.moveTo(p1.x, p1.y);
        ctx.lineTo(p2.x, p2.y);
        ctx.stroke();
      }
      for (let row = 0; row <= ceilingGridRows; row++) {
        const ny = bounds.minY + (row / ceilingGridRows) * (bounds.maxY - bounds.minY);
        const p1 = toCanvas(bounds.minX, ny, vp);
        const p2 = toCanvas(bounds.maxX, ny, vp);
        ctx.beginPath();
        ctx.moveTo(p1.x, p1.y);
        ctx.lineTo(p2.x, p2.y);
        ctx.stroke();
      }
      ctx.setLineDash([]);
      ctx.restore();
    };

    drawCeilingGrid();

    const drawCameraViewDirection = (cam: CameraSpec, idx: number) => {
      const marker = cameraMarkerNorm(cam, poolLengthM, poolWidthM, bounds);
      const { x, y } = toCanvas(marker.x, marker.y, vp);
      const isSel = selected === idx;
      const isDrag = isSel && !!canvasDrag;
      const isWall = isWallCameraSpec(cam);
      const headingRad = (Number(cam.heading_deg) * Math.PI) / 180;
      const halfFovRad = ((Number(cam.fov_h_deg) || 80) / 2) * (Math.PI) / 180;
      const spanNorm = Math.hypot(bounds.maxX - bounds.minX, bounds.maxY - bounds.minY);
      const lenNorm = spanNorm * (isDrag ? 0.28 : isSel ? 0.18 : 0.11);

      const pointAt = (angleRad: number) =>
        toCanvas(
          marker.x + Math.cos(angleRad) * lenNorm,
          marker.y + Math.sin(angleRad) * lenNorm,
          vp,
        );

      const axisEnd = pointAt(headingRad);
      const leftEdge = pointAt(headingRad - halfFovRad);
      const rightEdge = pointAt(headingRad + halfFovRad);
      const axisColor = isWall
        ? isSel
          ? '#0d47a1'
          : 'rgba(25, 118, 210, 0.9)'
        : isSel
          ? '#e65100'
          : 'rgba(245, 124, 0, 0.9)';
      const coneColor = isWall ? 'rgba(25, 118, 210, 0.4)' : 'rgba(245, 124, 0, 0.4)';

      ctx.save();
      ctx.lineCap = 'round';

      // 틸트 드래그는 바닥 발자국이 본체 — 평면 부채꼴은 약하게만
      if (isDrag && canvasDrag !== 'tilt') {
        ctx.beginPath();
        ctx.moveTo(x, y);
        ctx.lineTo(leftEdge.x, leftEdge.y);
        ctx.lineTo(rightEdge.x, rightEdge.y);
        ctx.closePath();
        ctx.fillStyle = isWall ? 'rgba(25, 118, 210, 0.22)' : 'rgba(245, 124, 0, 0.22)';
    ctx.fill();
        ctx.strokeStyle = axisColor;
        ctx.lineWidth = 2;
        ctx.stroke();
      }

      [headingRad - halfFovRad, headingRad + halfFovRad].forEach((ang) => {
        const edge = pointAt(ang);
        ctx.beginPath();
        ctx.moveTo(x, y);
        ctx.lineTo(edge.x, edge.y);
        ctx.strokeStyle = coneColor;
        ctx.lineWidth = isDrag ? 2.5 : isSel ? 1.5 : 1;
        ctx.setLineDash(isDrag ? [] : [4, 4]);
        ctx.stroke();
      });
      ctx.setLineDash([]);

      ctx.beginPath();
      ctx.moveTo(x, y);
      ctx.lineTo(axisEnd.x, axisEnd.y);
      ctx.strokeStyle = axisColor;
      ctx.lineWidth = isDrag ? 4 : isSel ? 3 : 2;
      ctx.stroke();

      const ah = isDrag ? 11 : isSel ? 9 : 7;
      const ang = Math.atan2(axisEnd.y - y, axisEnd.x - x);
      ctx.beginPath();
      ctx.moveTo(axisEnd.x, axisEnd.y);
      ctx.lineTo(
        axisEnd.x - ah * Math.cos(ang - Math.PI / 6),
        axisEnd.y - ah * Math.sin(ang - Math.PI / 6),
      );
      ctx.lineTo(
        axisEnd.x - ah * Math.cos(ang + Math.PI / 6),
        axisEnd.y - ah * Math.sin(ang + Math.PI / 6),
      );
      ctx.closePath();
      ctx.fillStyle = axisColor;
      ctx.fill();

      // 벽캠: 화살표에 카메라 기준 팬 표시 (0=정면, +우측, −좌측)
      if (isWall) {
        const side = inferWallSide(cam, bounds);
        const face = wallFaceHeadingDeg(side);
        const panDisp = absoluteToCameraPanDisplayDeg(Number(cam.heading_deg), face);
        const label = `${panDisp > 0 ? '+' : ''}${panDisp.toFixed(1)}°`;
        const lx = axisEnd.x + Math.cos(ang) * (isSel || isDrag ? 12 : 8);
        const ly = axisEnd.y + Math.sin(ang) * (isSel || isDrag ? 12 : 8);
        ctx.font = isDrag ? 'bold 12px sans-serif' : isSel ? 'bold 11px sans-serif' : '10px sans-serif';
        const tw = ctx.measureText(label).width;
        ctx.fillStyle = isSel || isDrag ? 'rgba(13, 71, 161, 0.9)' : 'rgba(25, 118, 210, 0.72)';
        ctx.fillRect(lx - tw / 2 - 3, ly - 7, tw + 6, 14);
        ctx.fillStyle = '#fff';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(label, lx, ly);
        if (isSel || isDrag) {
          const edgeOff = (Number(cam.fov_h_deg) || 80) / 2;
          (
            [
              [headingRad - halfFovRad, panDisp - edgeOff],
              [headingRad + halfFovRad, panDisp + edgeOff],
            ] as const
          ).forEach(([eAng, eDeg]) => {
            const ep = pointAt(eAng);
            const text = `${eDeg.toFixed(0)}°`;
            const etw = ctx.measureText(text).width;
            ctx.font = '10px sans-serif';
            ctx.fillStyle = 'rgba(25, 118, 210, 0.75)';
            ctx.fillRect(ep.x - etw / 2 - 3, ep.y - 7, etw + 6, 14);
            ctx.fillStyle = '#fff';
            ctx.fillText(text, ep.x, ep.y);
          });
        }
        ctx.textAlign = 'start';
        ctx.textBaseline = 'alphabetic';
      }

      ctx.restore();
    };

    ctx.save();
    ctx.beginPath();
    poolPolygon.forEach(([px, py], i) => {
      const { x, y } = toCanvas(px, py, vp);
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    });
    ctx.closePath();
    ctx.clip();

    if (result?.heatmap?.length) {
      const gs = result.heatmap.length;
      for (let iy = 0; iy < gs; iy++) {
        for (let ix = 0; ix < gs; ix++) {
          const v = result.heatmap[iy][ix];
          if (!v) continue;
          const nx0 = bounds.minX + (ix / gs) * (bounds.maxX - bounds.minX);
          const ny0 = bounds.minY + (iy / gs) * (bounds.maxY - bounds.minY);
          const nx1 = bounds.minX + ((ix + 1) / gs) * (bounds.maxX - bounds.minX);
          const ny1 = bounds.minY + ((iy + 1) / gs) * (bounds.maxY - bounds.minY);
          const c0 = toCanvas(nx0, ny0, vp);
          const c1 = toCanvas(nx1, ny1, vp);
          const alpha = heatmapAlphaForStrength(v);
          ctx.fillStyle = `rgba(46, 125, 50, ${alpha * 0.85})`;
          ctx.fillRect(c0.x, c0.y, c1.x - c0.x + 1, c1.y - c0.y + 1);
        }
      }
    }

    result?.footprints?.forEach((fp, idx) => {
      if (!fp?.polygon?.length) return;
      // 드래그 중인 카메라는 실시간 미리보기로 대체(틸트 반영)
      if (canvasDrag && selected === idx) return;
      const isSel = selected === idx;
      // 선택 시 다른 카메라는 옅게, 선택은 강하게
      const dimOthers = selected != null && !isSel;
      ctx.beginPath();
      fp.polygon.forEach(([px, py], i) => {
        const { x, y } = toCanvas(px, py, vp);
        if (i === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      });
      ctx.closePath();
      ctx.fillStyle = isSel
        ? 'rgba(255, 193, 7, 0.42)'
        : dimOthers
          ? 'rgba(255, 235, 59, 0.08)'
          : 'rgba(255, 235, 59, 0.22)';
      ctx.fill();
      ctx.strokeStyle = isSel ? '#f57f17' : dimOthers ? 'rgba(251, 192, 45, 0.35)' : '#fbc02d';
      ctx.lineWidth = isSel ? 3 : 1.75;
      ctx.stroke();
    });

    // 시뮬 결과 footprint가 없어도 선택 카메라는 로컬 화각(바닥 시야) 표시
    if (
      selected != null &&
      cameras[selected] &&
      !(canvasDrag && selected != null) &&
      !(result?.footprints?.[selected]?.polygon?.length)
    ) {
      const livePoly = previewFootprintPolygonNorm(
        cameras[selected],
        poolLengthM,
        poolWidthM,
      );
      if (livePoly.length >= 3) {
        ctx.beginPath();
        livePoly.forEach(([px, py], i) => {
          const { x, y } = toCanvas(px, py, vp);
          if (i === 0) ctx.moveTo(x, y);
          else ctx.lineTo(x, y);
        });
        ctx.closePath();
        ctx.fillStyle = 'rgba(255, 193, 7, 0.42)';
        ctx.fill();
        ctx.strokeStyle = '#f57f17';
        ctx.lineWidth = 3;
        ctx.stroke();
      }
    }

    // 드래그 중: 틸트·방위·초점이 반영된 바닥 시야를 즉시 그림
    if (canvasDrag && selected != null && cameras[selected]) {
      const livePoly = previewFootprintPolygonNorm(
        cameras[selected],
        poolLengthM,
        poolWidthM,
      );
      if (livePoly.length >= 3) {
        ctx.beginPath();
        livePoly.forEach(([px, py], i) => {
          const { x, y } = toCanvas(px, py, vp);
          if (i === 0) ctx.moveTo(x, y);
          else ctx.lineTo(x, y);
        });
        ctx.closePath();
        ctx.fillStyle = 'rgba(255, 152, 0, 0.42)';
        ctx.fill();
        ctx.strokeStyle = '#e65100';
        ctx.lineWidth = 3;
        ctx.stroke();
      }
    }

    cameras.forEach((cam, idx) => {
      drawCameraViewDirection(cam, idx);
    });

    ctx.restore();

    if (
      selected != null &&
      cameras[selected]
    ) {
      const handlePoly =
        canvasDrag && cameras[selected]
          ? previewFootprintPolygonNorm(cameras[selected], poolLengthM, poolWidthM)
          : result?.footprints?.[selected]?.polygon?.length
            ? result.footprints[selected].polygon
            : previewFootprintPolygonNorm(cameras[selected], poolLengthM, poolWidthM);
      const handles =
        handlePoly && handlePoly.length
          ? footprintHandlesOnCanvas(
              cameras[selected],
              handlePoly,
              (nx, ny) => toCanvas(nx, ny, vp),
              w,
              h,
              18,
              poolLengthM,
              poolWidthM,
              bounds,
            )
          : footprintHandlesOnCanvas(
              cameras[selected],
              [],
              (nx, ny) => toCanvas(nx, ny, vp),
              w,
              h,
              18,
              poolLengthM,
              poolWidthM,
              bounds,
            );
      visibleHandlesRef.current = handles || [];
      if (handles) {
        const colors: Record<FootprintHandleCanvas['id'], string> = {
          heading: '#1565c0',
          tilt: '#ef6c00',
          focal: '#2e7d32',
        };
        const hoverId = hoverHandleIdRef.current;
        const dragCam = cameras[selected];
        for (const handle of handles) {
          const active = hoverId === handle.id || canvasDrag === handle.id;
          if (active) {
            drawHandleDragAxis(ctx, handle, colors[handle.id], 36);
          }
      ctx.beginPath();
          ctx.arc(handle.x, handle.y, active ? 9 : 7, 0, Math.PI * 2);
          ctx.fillStyle = colors[handle.id];
      ctx.fill();
          ctx.strokeStyle = '#fff';
          ctx.lineWidth = 2;
          ctx.stroke();
        }
        // 드래그 중: 현재 수치 + 시야 범위 강조 안내
        if (canvasDrag && dragCam) {
          const marker = cameraMarkerNorm(dragCam, poolLengthM, poolWidthM, bounds);
          const camP = toCanvas(marker.x, marker.y, vp);
          let label = '';
          if (canvasDrag === 'heading') {
            if (isWallCameraSpec(dragCam)) {
              const side = inferWallSide(dragCam, bounds);
              const disp = absoluteToCameraPanDisplayDeg(
                Number(dragCam.heading_deg),
                wallFaceHeadingDeg(side),
              );
              label = `팬 ${disp > 0 ? '+' : ''}${disp.toFixed(1)}° (0=정면)`;
            } else {
              label = `팬 ${Number(dragCam.heading_deg).toFixed(1)}° (도면)`;
            }
          } else if (canvasDrag === 'tilt') {
            label = `틸트 ${Number(dragCam.tilt_deg).toFixed(1)}°`;
          } else if (canvasDrag === 'focal') {
            label = `줌 ${Number(dragCam.focal_length_mm ?? defaultFocalLengthMm).toFixed(2)} mm  (H ${Number(dragCam.fov_h_deg).toFixed(1)}° / V ${Number(dragCam.fov_v_deg).toFixed(1)}°)`;
          } else if (canvasDrag === 'camera') {
            label = `위치 (${Number(dragCam.x).toFixed(1)}, ${Number(dragCam.y).toFixed(1)})`;
          }
          if (label) {
            ctx.save();
            ctx.font = 'bold 13px sans-serif';
            const tw = ctx.measureText(label).width;
            const bx = Math.min(w - tw - 16, Math.max(8, camP.x + 14));
            const by = Math.max(22, camP.y - 18);
            ctx.fillStyle = 'rgba(33, 33, 33, 0.82)';
            ctx.fillRect(bx - 6, by - 14, tw + 12, 22);
      ctx.fillStyle = '#fff';
            ctx.fillText(label, bx, by + 2);
            ctx.restore();
          }
        }
        if (hoverId === 'camera' || canvasDrag === 'camera') {
          const marker = cameraMarkerNorm(cameras[selected], poolLengthM, poolWidthM, bounds);
          const camP = toCanvas(marker.x, marker.y, vp);
          ctx.save();
          ctx.strokeStyle = '#e65100';
          ctx.fillStyle = '#e65100';
          ctx.lineWidth = 2.5;
          ctx.lineCap = 'round';
          const L = 26;
          ctx.beginPath();
          ctx.moveTo(camP.x - L, camP.y);
          ctx.lineTo(camP.x + L, camP.y);
          ctx.moveTo(camP.x, camP.y - L);
          ctx.lineTo(camP.x, camP.y + L);
          ctx.stroke();
          ctx.restore();
        }
      }
    } else {
      visibleHandlesRef.current = [];
    }

    cameras.forEach((cam, idx) => {
      const marker = cameraMarkerNorm(cam, poolLengthM, poolWidthM, bounds);
      const { x, y } = toCanvas(marker.x, marker.y, vp);
      const isSel = selected === idx;
      const isWall = isWallCameraSpec(cam);

      if (isWall) {
        ctx.fillStyle = isSel ? '#1565c0' : '#42a5f5';
        ctx.fillRect(x - 7, y - 7, 14, 14);
        ctx.strokeStyle = '#fff';
        ctx.lineWidth = 2;
        ctx.strokeRect(x - 7, y - 7, 14, 14);
      } else {
        ctx.fillStyle = isSel ? '#e65100' : '#ff9800';
        ctx.beginPath();
        ctx.arc(x, y, isSel ? 10 : 7, 0, Math.PI * 2);
        ctx.fill();
        ctx.strokeStyle = '#fff';
        ctx.lineWidth = 2;
        ctx.stroke();
      }

      ctx.fillStyle = '#fff';
      ctx.font = 'bold 11px sans-serif';
      ctx.fillText(String(idx + 1), x - (idx + 1 >= 10 ? 5 : 3), y + 4);
    });

    const fmtM = (v: number) => formatMeters1(v);

    const drawHorizSeg = (
      xLeft: number,
      xRight: number,
      yAnchor: number,
      yDim: number,
      meters: number,
    ) => {
      if (meters < MIN_DIM_M || Math.abs(xRight - xLeft) < 4) return;
      drawHorizontalDimension(ctx, xLeft, xRight, yAnchor, yDim, fmtM(meters));
    };

    const drawVertSeg = (
      yTop: number,
      yBottom: number,
      xAnchor: number,
      xDim: number,
      meters: number,
    ) => {
      if (meters < MIN_DIM_M || Math.abs(yBottom - yTop) < 4) return;
      drawVerticalDimension(ctx, yTop, yBottom, xAnchor, xDim, fmtM(meters));
    };

    drawHorizontalDimension(
      ctx,
      poolBL.x,
      poolBR.x,
      poolBL.y,
      poolBL.y + DIM_GAP_TOTAL,
      fmtM(poolLengthM),
    );

    drawVerticalDimension(
      ctx,
      poolTR.y,
      poolBR.y,
      poolBR.x,
      poolBR.x + DIM_GAP_WIDTH,
      fmtM(poolWidthM),
    );

    const edgeBand = spanNormY * 0.18;
    const nearTop = (cy: number) => cy <= bounds.minY + edgeBand;
    const nearBottom = (cy: number) => cy >= bounds.maxY - edgeBand;

    /** 한 벽 줄: 가로 치수를 **한 높이**에 정렬 (좌·중·우) — 마커 X 기준 */
    const dimCameraRowAlongLength = (
      list: CameraSpec[],
      edgeNormY: number,
      above: boolean,
    ) => {
      if (!list.length) return;
      const sorted = [...list].sort((a, b) => a.x - b.x);
      const pEdgeLeft = toCanvas(bounds.minX, edgeNormY, vp);
      const pEdgeRight = toCanvas(bounds.maxX, edgeNormY, vp);
      const yAnchor = pEdgeLeft.y;
      const yDim = above ? yAnchor - DIM_GAP_ROW : yAnchor + DIM_GAP_ROW;

      const markerX = (cam: CameraSpec) =>
        cameraMarkerNorm(cam, poolLengthM, poolWidthM, bounds).x;

      const first = sorted[0];
      const last = sorted[sorted.length - 1];
      const pFirst = toCanvas(markerX(first), edgeNormY, vp);
      const pLast = toCanvas(markerX(last), edgeNormY, vp);

      drawHorizSeg(
        pEdgeLeft.x,
        pFirst.x,
        yAnchor,
        yDim,
        poolSpanMAlongX(bounds.minX, first.x),
      );

      for (let i = 0; i < sorted.length - 1; i++) {
        const a = sorted[i];
        const b = sorted[i + 1];
        const pa = toCanvas(markerX(a), edgeNormY, vp);
        const pb = toCanvas(markerX(b), edgeNormY, vp);
        drawHorizSeg(pa.x, pb.x, yAnchor, yDim, poolSpanMAlongX(a.x, b.x));
      }

      drawHorizSeg(
        pLast.x,
        pEdgeRight.x,
        yAnchor,
        yDim,
        poolSpanMAlongX(last.x, bounds.maxX),
      );
    };

    /**
     * 상·하 벽캠: 벽↔카메라(설치 돌출) · 풀↔카메라 거리.
     * 각 장변 왼쪽 끝 CCTV에 세로 치수로 표시(오른쪽 12m와 분리).
     */
    const dimWallAndPoolCameraOffset = (
      list: CameraSpec[],
      edgeNormY: number,
    ) => {
      if (!list.length) return;
      const sample = list[0];
      const wallGapM =
        Number(sample.pool_edge_offset_m) ||
        wallPoolEdgeForSide(wallPoolEdgeOffsetsM, inferWallSide(sample, bounds));
      const mountM = Number(sample.wall_mount_offset_m) || wallMountOffsetM;
      const gapM = wallCameraToPoolGapM(wallGapM, mountM);
      const isTop = edgeNormY <= (bounds.minY + bounds.maxY) / 2;
      const wallAwayNorm = isTop
        ? edgeNormY - (wallGapM / poolWidthM) * spanNormY
        : edgeNormY + (wallGapM / poolWidthM) * spanNormY;

      const cam = list.reduce((a, b) => (a.x <= b.x ? a : b));
      const marker = cameraMarkerNorm(cam, poolLengthM, poolWidthM, bounds);
      const pPool = toCanvas(marker.x, edgeNormY, vp);
      const pCam = toCanvas(marker.x, marker.y, vp);
      const pWall = toCanvas(marker.x, wallAwayNorm, vp);
      const xAnchor = pCam.x;
      const xDimPool = Math.min(poolTL.x - DIM_GAP_WALL_CAM, xAnchor - 14);
      const xDimWall = xDimPool - 16;

      // 벽 ↔ 카메라 (기본 0.2m — 누락되던 치수)
      if (mountM >= 0.05) {
        drawVertSeg(pWall.y, pCam.y, xAnchor, xDimWall, mountM);
      }
      // 풀 가장자리 ↔ 카메라 (기본 0.8m)
      if (gapM >= 0.05) {
        drawVertSeg(pCam.y, pPool.y, xAnchor, xDimPool, gapM);
      }
    };

    /** 행이 2줄 이상일 때 **행간**만 왼쪽 한 줄에 표기 (풀 폭 12m는 오른쪽만) */
    const dimRowSpacingAlongWidth = (list: CameraSpec[]) => {
      if (!list.length) return;
      const rowYs = [...new Set(list.map((c) => c.y))].sort((a, b) => a - b);
      if (rowYs.length < 2) return;

      const xAnchor = poolTL.x;
      const xDim = xAnchor - DIM_GAP_ROW_VERT;

      for (let i = 0; i < rowYs.length - 1; i++) {
        const ya = rowYs[i];
        const yb = rowYs[i + 1];
        const pa = toCanvas(bounds.minX, ya, vp);
        const pb = toCanvas(bounds.minX, yb, vp);
        drawVertSeg(pa.y, pb.y, xAnchor, xDim, poolSpanMAlongY(ya, yb));
      }
    };

    const wallCams = cameras.filter((c) => c.mount_type === 'wall');
    const topRow = wallCams.filter((c) => nearTop(c.y));
    const botRow = wallCams.filter((c) => nearBottom(c.y));
    if (topRow.length) {
      dimCameraRowAlongLength(topRow, bounds.minY, true);
      dimWallAndPoolCameraOffset(topRow, bounds.minY);
    }
    if (botRow.length) {
      dimCameraRowAlongLength(botRow, bounds.maxY, false);
      dimWallAndPoolCameraOffset(botRow, bounds.maxY);
    }
    if (wallCams.length >= 2) dimRowSpacingAlongWidth(wallCams);

    const ceilingCams = cameras.filter((c) => c.mount_type !== 'wall');
    if (ceilingCams.length) {
      if (ceilingCams.length >= 2) dimRowSpacingAlongWidth(ceilingCams);

      const byRow = new Map<number, CameraSpec[]>();
      for (const cam of ceilingCams) {
        const rowKey = Math.round(cam.y * 10) / 10;
        if (!byRow.has(rowKey)) byRow.set(rowKey, []);
        byRow.get(rowKey)!.push(cam);
      }
      for (const row of byRow.values()) {
        if (!row.length) continue;
        dimCameraRowAlongLength(row, row[0].y, true);
      }
    }
  }, [
    cameras,
    result,
    selected,
    poolPolygon,
    poolLengthM,
    poolWidthM,
    laneCount,
    ceilingGridCols,
    ceilingGridRows,
    canvasDrag,
    wallPoolEdgeOffsetsM,
    wallMountOffsetM,
    resolveView,
  ]);

  useEffect(() => {
    draw();
  }, [draw]);

  const canvasPoint = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const c = canvasRef.current!;
    const rect = c.getBoundingClientRect();
    const cx = ((e.clientX - rect.left) / rect.width) * c.width;
    const cy = ((e.clientY - rect.top) / rect.height) * c.height;
    const { vp, viewBounds, fitScale } = resolveView();
    return {
      ...canvasToNormInBounds(cx, cy, vp, viewBounds, fitScale),
      vp,
      cx,
      cy,
    };
  };

  const hitIndex = (cx: number, cy: number, vp: ReturnType<typeof getPoolViewport>) => {
    const { poolBounds: bounds, viewBounds, fitScale } = resolveView();
    let best: number | null = null;
    let bestDist = 28; // 번호 마커 클릭 용이하게
    cameras.forEach((cam, i) => {
      const marker = cameraMarkerNorm(cam, poolLengthM, poolWidthM, bounds);
      const { x, y } = normToCanvasInBounds(marker.x, marker.y, vp, viewBounds, fitScale);
      const dPx = Math.hypot(x - cx, y - cy);
      if (dPx <= bestDist) {
        bestDist = dPx;
        best = i;
      }
    });
    return best;
  };

  const selectCamera = useCallback(
    (idx: number, reason?: string) => {
      if (idx < 0 || idx >= cameras.length) return;
      selectedRef.current = idx;
      setSelected(idx);
      const cam = cameras[idx];
      const label = cam.name || `CCTV ${idx + 1}`;
      let panLabel = `${cam.heading_deg}°`;
      if (isWallCameraSpec(cam)) {
        const bounds = poolBoundsFromPolygon(poolPolygon);
        const side = inferWallSide(cam, bounds);
        const disp = absoluteToCameraPanDisplayDeg(
          Number(cam.heading_deg),
          wallFaceHeadingDeg(side),
        );
        panLabel = `${disp > 0 ? '+' : ''}${disp}°(0=정면)`;
      }
      setMsg(
        reason ||
          `「${label}」 선택 — 노란 영역=화각·커버리지. HFOV ${cam.fov_h_deg ?? '-'}° / VFOV ${cam.fov_v_deg ?? '-'}° / 틸트 ${cam.tilt_deg}° / 팬 ${panLabel}.`,
      );
      requestAnimationFrame(() => {
        canvasRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      });
    },
    [cameras, poolPolygon],
  );

  const applyCams = (next: CameraSpec[], sel: number | null = selected) => {
    commitCameras(next, sel);
  };

  const coverageFill =
    (ceilingGridRows > 0 && ceilingGridCols > 0) ||
    (wallGridRows > 0 && wallGridCols > 0) ||
    wallLeftCount > 0 ||
    wallRightCount > 0;

  /** 위치만 바뀔 때 팬을 그 자리 앞으로. 직접 입력한 팬은 유지. */
  const withMovedHeading = (index: number, moved: CameraSpec, peers: CameraSpec[]): CameraSpec => {
    if (manualDerivedRef.current.get(index)?.heading_deg != null) return moved;
    return {
      ...moved,
      heading_deg: headingDegAfterPositionMove(moved, peers, poolLengthM, poolWidthM, {
        installHeightM,
        wallRows: wallGridRows,
        wallCols: wallGridCols,
        fillCoverage: coverageFill,
      }),
    };
  };

  /** 위치·렌즈는 유지하고 팬·틸트만 현재 입력으로 다시 맞춤. 직접 입력한 각도는 유지. */
  const realignPanTilt = () => {
    if (!cameras.length) return;
    const opts = {
      installHeightM,
      wallRows: wallGridRows,
      wallCols: wallGridCols,
      fillCoverage: coverageFill,
    };
    const next = cameras.map((cam, i) => {
      const pose = aimPoseAfterPosition(cam, cameras, poolLengthM, poolWidthM, opts);
      const manual = manualDerivedRef.current.get(i);
      return {
        ...cam,
        heading_deg: manual?.heading_deg != null ? cam.heading_deg : pose.heading_deg,
        tilt_deg: manual?.tilt_deg != null ? cam.tilt_deg : pose.tilt_deg,
      };
    });
    commitCameras(next, selected);
    setMsg('팬·틸트를 현재 위치·높이·벽 거리에 맞춰 다시 계산함. 직접 입력한 각도는 유지. 위치·렌즈는 그대로.');
  };

  const commitAlongMeters = (index: number, meters: number) => {
    const bounds = poolBoundsFromPolygon(poolPolygon);
    setCameras((prev) => {
      const cam = prev[index];
      if (!cam) return prev;
      const side = inferWallSide(cam, bounds);
      const pos = normFromAlongEdgeMeters(cam, side, meters, bounds, poolLengthM, poolWidthM);
      const moved: CameraSpec = { ...cam, x: pos.x, y: pos.y };
      const peers = prev.map((c, i) => (i === index ? moved : c));
      const nextCam = withMovedHeading(index, moved, peers);
      const next = peers.map((c, i) => (i === index ? nextCam : c));
      scheduleSim(next, poolPolygon, poolLengthM, poolWidthM, 120);
      return next;
    });
  };

  const patchCamFromCanvasDrag = (
    cam: CameraSpec,
    index: number,
    nx: number,
    ny: number,
    kind: CanvasHandleHit,
  ): CameraSpec => {
    if (kind === 'camera') {
      const bounds = poolPolygonBounds(poolPolygon);
      const stored = cameraStoredNormFromMarker(
        nx,
        ny,
        cam,
        poolLengthM,
        poolWidthM,
        bounds,
      );
      return { ...cam, x: stored.x, y: stored.y };
    }
    if (kind === 'heading') {
      const bounds = poolPolygonBounds(poolPolygon);
      const heading_deg = normHeadingDegFromPoint(
        cam,
        nx,
        ny,
        poolLengthM,
        poolWidthM,
        bounds,
      );
      manualDerivedRef.current.set(index, {
        ...manualDerivedRef.current.get(index),
        heading_deg,
      });
      return normalizeCam({ ...cam, heading_deg }, defaultFocalLengthMm);
    }
    if (kind === 'focal') {
      const bounds = poolPolygonBounds(poolPolygon);
      const fov_h_deg = horizontalFovDegFromPoint(
        cam,
        nx,
        ny,
        poolLengthM,
        poolWidthM,
        bounds,
      );
      const o = applyCameraOptics({
        mount_type: cam.mount_type,
        fov_h_deg,
        resolution_w: cam.resolution_w ?? resolutionW,
        resolution_h: cam.resolution_h ?? resolutionH,
      });
      manualOpticsLockRef.current.add(index);
      manualFocalLocksRef.current.add(index);
      manualDerivedRef.current.set(index, {
        ...manualDerivedRef.current.get(index),
        focal_length_mm: o.focal_length_mm,
      });
      return normalizeCam({ ...cam, focal_length_mm: o.focal_length_mm }, o.focal_length_mm);
    }
    if (kind === 'tilt') {
      const bounds = poolPolygonBounds(poolPolygon);
      const tilt_deg = tiltDegFromGroundPoint(
        cam,
        nx,
        ny,
        poolLengthM,
        poolWidthM,
        bounds,
      );
      manualDerivedRef.current.set(index, {
        ...manualDerivedRef.current.get(index),
        tilt_deg,
      });
      return normalizeCam({ ...cam, tilt_deg }, defaultFocalLengthMm);
    }
    return cam;
  };

  const onPointerDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const { x, y, vp, cx, cy } = canvasPoint(e);
    const { poolBounds: bounds, viewBounds, fitScale } = resolveView();

    if (selected != null && cameras[selected]) {
      const handles = visibleHandlesRef.current;
      const markerSel = cameraMarkerNorm(
        cameras[selected],
        poolLengthM,
        poolWidthM,
        bounds,
      );
      const camP = normToCanvasInBounds(
        markerSel.x,
        markerSel.y,
        vp,
        viewBounds,
        fitScale,
      );
      const handleHit = hitFootprintHandleCanvas(cx, cy, handles, camP, 16);
      if (handleHit) {
          setCanvasDrag(handleHit);
          setDragging(true);
          setAddMode(false);
          const labels: Record<CanvasHandleHit, string> = {
            camera: '위치',
            heading: '팬',
            tilt: '틸트',
            focal: '줌',
          };
          hoverHandleIdRef.current = handleHit;
          setHoverTipStable(CANVAS_HANDLE_TIPS[handleHit]);
          setMsg(
            `「${cameras[selected].name || selected + 1}」 ${labels[handleHit]} 핸들 — 드래그하여 팬·틸트·줌`,
          );
          draw();
        return;
      }
    }

    const hit = hitIndex(cx, cy, vp);
    if (hit != null) {
      selectCamera(
        hit,
        `「${cameras[hit].name || hit + 1}」(${cameras[hit].mount_type === 'wall' ? '벽' : '천장'}) — 이동 또는 노란 박스 핸들로 팬·틸트·줌`,
      );
      setCanvasDrag('camera');
      setDragging(true);
      setAddMode(false);
      return;
    }
    if (!addMode) {
      setMsg('주황(천장)/파란(벽) 마커를 클릭해 선택. 추가는 「카메라 추가」를 켬.');
      return;
    }
    if (!isNormInsidePoolBounds(x, y, bounds)) {
      setMsg('시설 도면 안쪽을 클릭해 카메라를 배치');
      return;
    }
    const nextCam = normalizeCam(
      {
        ...MOUNT_DEFAULTS[addMount],
        x: Math.round(x * 10) / 10,
        y: Math.round(y * 10) / 10,
        heading_deg: addMount === 'wall' ? (x < 50 ? 0 : 180) : 90,
        name: `${addMount === 'wall' ? '벽' : '천장'}캠 ${cameras.length + 1}`,
        mount_type: addMount,
      },
      defaultFocalLengthMm,
    );
    const next = [...cameras, nextCam];
    applyCams(next, next.length - 1);
    setAddMode(false);
    setMsg('카메라가 추가됨');
  };

  const setHoverTipStable = (next: string | null) => {
    if (hoverTipRef.current === next) return;
    hoverTipRef.current = next;
    const el = hoverTipElRef.current;
    if (el) {
      if (next) {
        el.textContent = next;
        el.style.display = 'block';
      } else {
        el.textContent = '';
        el.style.display = 'none';
      }
    }
  };

  const onPointerMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    const { x, y, vp, cx, cy } = canvasPoint(e);

    if (dragging && selected != null && canvasDrag) {
      setCameras((prev) => {
        const next = prev.map((cam, i) => {
          if (i !== selected) return cam;
          const patched = patchCamFromCanvasDrag(cam, i, x, y, canvasDrag);
          if (canvasDrag !== 'camera') return patched;
          const peers = prev.map((c, j) => (j === i ? patched : c));
          return withMovedHeading(i, patched, peers);
        });
        // 드래그 중에도 시야(노란 영역·커버)가 보이도록 짧게 디바운스 재계산
        scheduleSim(next, poolPolygon, poolLengthM, poolWidthM, 120);
        return next;
      });
      if (canvas) canvas.style.cursor = 'grabbing';
      return;
    }

    let tip: string | null = null;
    let cursor = addMode ? 'cell' : 'pointer';
    let hoverId: CanvasHandleHit | null = null;
    if (selected != null && cameras[selected]) {
      const handles = visibleHandlesRef.current;
      const { poolBounds: boundsMove, viewBounds, fitScale } = resolveView();
      const markerSel = cameraMarkerNorm(
        cameras[selected],
        poolLengthM,
        poolWidthM,
        boundsMove,
      );
      const camP = normToCanvasInBounds(
        markerSel.x,
        markerSel.y,
        vp,
        viewBounds,
        fitScale,
      );
      const handleHit = hitFootprintHandleCanvas(cx, cy, handles, camP, 16);
      if (handleHit) {
        tip = CANVAS_HANDLE_TIPS[handleHit];
        cursor = 'grab';
        hoverId = handleHit;
      }
    }
    if (!tip) {
      const camHit = hitIndex(cx, cy, vp);
      if (camHit != null) {
        tip = CANVAS_HANDLE_TIPS.camera;
        cursor = 'grab';
        hoverId = 'camera';
      }
    }
    if (canvas) canvas.style.cursor = cursor;
    setHoverTipStable(tip);
    if (hoverHandleIdRef.current !== hoverId) {
      hoverHandleIdRef.current = hoverId;
      draw();
    }
  };

  const onPointerUp = () => {
    if (!dragging) return;
    setDragging(false);
    const dragKind = canvasDrag;
    setCanvasDrag(null);
    hoverHandleIdRef.current = null;
    setHoverTipStable(null);
    setCameras((prev) => {
      scheduleSim(prev, poolPolygon, poolLengthM, poolWidthM);
      return prev;
    });
    if (dragKind === 'camera') {
      const locked =
        selected != null && manualDerivedRef.current.get(selected)?.heading_deg != null;
      setMsg(
        locked
          ? '위치를 옮김. 직접 맞춘 팬은 유지.'
          : '위치를 옮김. 팬은 그 자리 앞 수면을 보도록 다시 계산함.',
      );
    } else if (dragKind) {
      setMsg('팬·틸트·줌을 반영함. 노란 시야 영역과 표의 수치를 확인.');
    }
  };

  const onPointerLeave = () => {
    setHoverTipStable(null);
    if (hoverHandleIdRef.current != null) {
      hoverHandleIdRef.current = null;
      draw();
    }
    if (canvasRef.current) {
      canvasRef.current.style.cursor = addMode ? 'cell' : 'pointer';
    }
    if (dragging) onPointerUp();
  };

  const removeSelected = () => {
    if (selected == null) return;
    const next = cameras.filter((_, i) => i !== selected);
    applyCams(next, next.length ? Math.min(selected, next.length - 1) : null);
  };

  const handleSave = async () => {
    setLoading(true);
    setErr(null);
    try {
      const nWall = cameras.filter((c) => c.mount_type === 'wall').length;
      const nCeil = cameras.filter((c) => c.mount_type === 'ceiling').length;
      const mountMode: 'ceiling' | 'wall' =
        nWall > nCeil || wallGridRows * wallGridCols > 0 || wallLeftCount + wallRightCount > 0
          ? 'wall'
          : 'ceiling';
      await saveCameraLayout(poolId, {
        pool_polygon: poolPolygon,
        cameras,
        pool_length_m: poolLengthM,
        pool_width_m: poolWidthM,
        lane_count: laneCount,
        ceiling_grid_rows: ceilingGridRows,
        ceiling_grid_cols: ceilingGridCols,
        wall_grid_rows: wallGridRows,
        wall_grid_cols: wallGridCols,
        wall_left_count: wallLeftCount,
        wall_right_count: wallRightCount,
        wall_pool_edge_offset_m: wallPoolEdgeOffsetsM.top,
        wall_pool_edge_offset_top_m: wallPoolEdgeOffsetsM.top,
        wall_pool_edge_offset_bottom_m: wallPoolEdgeOffsetsM.bottom,
        wall_pool_edge_offset_left_m: wallPoolEdgeOffsetsM.left,
        wall_pool_edge_offset_right_m: wallPoolEdgeOffsetsM.right,
        wall_mount_offset_m: wallMountOffsetM,
        default_focal_length_mm: defaultFocalLengthMm,
        default_fov_h_deg: defaultFovHDeg,
        default_fov_v_deg: defaultFovVDeg,
        lens_fixed: lensFixed,
        mount_mode: mountMode,
      });
      onLayoutHydrated?.({
        poolLengthM,
        poolWidthM,
        mountMode,
        lensFixed,
        focalLengthMm: defaultFocalLengthMm,
        installHeightM,
      });
      setMsg('저장 완료. 시설 실측·높이·하향각·설치 방식·벽면/천장이 함께 저장됨.');
    } catch (e: any) {
      setErr(e?.response?.data?.detail || e?.message || '저장 실패');
    } finally {
      setLoading(false);
    }
  };

  const onDimChange = (len: number, wid: number) => {
    setPoolLengthM(len);
    setPoolWidthM(wid);
    setPoolPolygon(defaultPoolPolygonRect(0.035));
  };

  const onLaneChange = (lanes: number) => {
    const n = Math.max(1, Math.min(16, Math.floor(lanes) || 1));
    setLaneCount(n);
  };

  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom>
        카메라 배치 (3D 시야 투영)
      </Typography>

      <Alert severity="info" sx={{ mb: 2 }}>
        <Typography variant="body2">
          <strong>아래 도면</strong> — 선택 CCTV 핸들(실제 카메라와 동일):
          파랑=팬, 주황=틸트, 초록=줌, 본체=설치 위치.
          드래그하는 동안 노란 시야 범위와 수치가 실시간으로 보.
      </Typography>
      </Alert>

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
          기초 입력 · 화각 계산 조건 (도면 위)
        </Typography>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={1.25}
          flexWrap="wrap"
          useFlexGap
          alignItems="flex-start"
        >
          <FieldTip title="도면 가로(길이) 실측(m). 레인·벽캠 간격 계산의 기준이 됨.">
            <DraftNumberField
              label="길이 m"
              size="small"
              value={poolLengthM}
              onValueChange={(v) => onDimChange(v, poolWidthM)}
              min={1}
              max={100}
              emptyFallback={DEFAULT_POOL_LENGTH_M}
              inputProps={{ min: 1, max: 100, step: 0.5 }}
              sx={COMPACT_INPUT_SX}
            />
          </FieldTip>
          <FieldTip title="도면 세로(폭) 실측(m). 벽캠이 자벽에서 얼마나 안쪽까지 볼지 깊이에 영향.">
            <DraftNumberField
              label="폭 m"
              size="small"
              value={poolWidthM}
              onValueChange={(v) => onDimChange(poolLengthM, v)}
              min={1}
              max={50}
              emptyFallback={DEFAULT_POOL_WIDTH_M}
              inputProps={{ min: 1, max: 50, step: 0.5 }}
              sx={COMPACT_INPUT_SX}
            />
          </FieldTip>
          <FieldTip title="레인 개수. 도면 폭을 레인으로 나눠 참고 치수(레인 폭)를 표시. 커버리지 계산과는 무관.">
            <DraftNumberField
              label="레인"
              size="small"
              value={laneCount}
              onValueChange={(v) => onLaneChange(Math.round(v))}
              min={1}
              max={16}
              emptyFallback={DEFAULT_LANE_COUNT}
              inputProps={{ min: 1, max: 16, step: 1 }}
              sx={COMPACT_INPUT_SX}
            />
          </FieldTip>
          <FieldTip title="모든 CCTV 설치 높이(수면~렌즈, m). 높을수록 같은 화각으로 더 넓은 바닥을 덮지만 픽셀당 선예도는 떨어짐.">
            <DraftNumberField
              label="설치 높이 m"
              size="small"
              value={installHeightM}
              onValueChange={setInstallHeightM}
              min={1.5}
              max={8}
              emptyFallback={4.5}
              helperText="모든 CCTV 동일"
              inputProps={{ min: 1.5, max: 8, step: 0.1 }}
              sx={COMPACT_INPUT_MD_SX}
            />
          </FieldTip>
          {(
            [
              ['top', '상'],
              ['bottom', '하'],
              ['left', '좌'],
              ['right', '우'],
            ] as const
          ).map(([side, label]) => (
            <FieldTip
              key={side}
              title={`풀 수면 가장자리 ↔ ${label}쪽 벽 거리(데크, m). 면별로 다르기 가능`}
            >
              <DraftNumberField
                label={`벽·풀 ${label}`}
                size="small"
                value={wallPoolEdgeOffsetsM[side]}
                onValueChange={(v) => setWallPoolEdgeSide(side, v)}
                min={0}
                max={10}
                emptyFallback={0}
                inputProps={{ min: 0, max: 10, step: 0.1 }}
                sx={COMPACT_INPUT_MD_SX}
              />
            </FieldTip>
          ))}
          <FieldTip title="벽에서 카메라가 풀 쪽으로 나온 길이(m). 카메라↔풀 = 벽·풀 − 이 값. 예: 1.0 − 0.2 = 0.8m.">
            <DraftNumberField
              label="벽·캠 간격"
              size="small"
              value={wallMountOffsetM}
              onValueChange={setWallMountOffsetM}
              min={0}
              max={5}
              emptyFallback={0}
              inputProps={{ min: 0, max: 5, step: 0.05 }}
              sx={COMPACT_INPUT_MD_SX}
            />
          </FieldTip>
          <FieldTip title="천장 카메라 격자 — 세로 방향 칸 수(행). 0이면 천장캠 자동 배치 없음. 「자동 배치」 시 사용.">
            <DraftNumberField
              label="천장 행"
              size="small"
              value={ceilingGridRows}
              onValueChange={(v) => setCeilingGridRows(Math.round(v))}
              min={0}
              max={12}
              emptyFallback={0}
              inputProps={{ min: 0, max: 12 }}
              sx={CEILING_INPUT_SX}
            />
          </FieldTip>
          <FieldTip title="천장 카메라 격자 — 가로 방향 칸 수(열). 행×열 = 천장캠 대수.">
            <DraftNumberField
              label="천장 열"
              size="small"
              value={ceilingGridCols}
              onValueChange={(v) => setCeilingGridCols(Math.round(v))}
              min={0}
              max={12}
              emptyFallback={0}
              inputProps={{ min: 0, max: 12 }}
              sx={CEILING_INPUT_SX}
            />
          </FieldTip>
          <FieldTip title="벽 카메라 — 긴 변(윗·아랫) 가로 줄 수. 0이면 윗·아랫 배치 없음. 양쪽에 두려면 보통 2.">
            <DraftNumberField
              label="벽 행"
              size="small"
              value={wallGridRows}
              onValueChange={(v) => setWallGridRows(Math.round(v))}
              min={0}
              max={12}
              emptyFallback={0}
              helperText="윗·아랫(가로)"
              inputProps={{ min: 0, max: 12 }}
              sx={WALL_INPUT_SX}
            />
          </FieldTip>
          <FieldTip title="벽 카메라 — 긴 변 한 줄에 나란히 둘 대수(열). 벽 행과 함께 써야 가로 배치가 됨.">
            <DraftNumberField
              label="벽 열"
              size="small"
              value={wallGridCols}
              onValueChange={(v) => setWallGridCols(Math.round(v))}
              min={0}
              max={12}
              emptyFallback={0}
              helperText="윗·아랫 한 줄당"
              inputProps={{ min: 0, max: 12 }}
              sx={WALL_INPUT_SX}
            />
          </FieldTip>
          <FieldTip title="왼쪽 짧은 변(좌벽)에 설치할 벽캠 대수. 0=없음. 행·열과 독립(좌·우만 / 가로만 / 둘 다 가능).">
            <DraftNumberField
              label="좌벽"
              size="small"
              value={wallLeftCount}
              onValueChange={(v) => setWallLeftCount(Math.round(v))}
              min={0}
              max={12}
              emptyFallback={0}
              helperText="왼쪽 벽 대수"
              inputProps={{ min: 0, max: 12 }}
              sx={WALL_INPUT_SX}
            />
          </FieldTip>
          <FieldTip title="오른쪽 짧은 변(우벽)에 설치할 벽캠 대수. 0=없음.">
            <DraftNumberField
              label="우벽"
              size="small"
              value={wallRightCount}
              onValueChange={(v) => setWallRightCount(Math.round(v))}
              min={0}
              max={12}
              emptyFallback={0}
              helperText="오른쪽 벽 대수"
              inputProps={{ min: 0, max: 12 }}
              sx={WALL_INPUT_SX}
            />
          </FieldTip>
        </Stack>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.25} flexWrap="wrap" useFlexGap sx={{ mt: 1.25 }}>
          <FieldTip title="고정: 초점 mm만 쓰고 H/V는 그 값에서만 파생(자동 배치가 화각을 넓히지 않음). 가변: 커버에 맞춰 줌·H/V 조정.">
            <FormControl component="fieldset" variant="standard" sx={{ minWidth: 200 }}>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.25 }}>
                렌즈
              </Typography>
              <RadioGroup
                row
                value={lensFixed ? 'fixed' : 'variable'}
                onChange={(_, v) => setLensFixed(v === 'fixed')}
              >
                <FormControlLabel value="fixed" control={<Radio size="small" />} label="고정" />
                <FormControlLabel value="variable" control={<Radio size="small" />} label="가변(줌)" />
              </RadioGroup>
            </FormControl>
          </FieldTip>
          <FieldTip title="렌즈 초점거리(mm). 고정 모드에서는 이 값만 입력하고 H/V는 파생·잠금. 작을수록 광각, 클수록 망원.">
            <DraftNumberField
              label={lensFixed ? '고정 초점 (mm)' : '기본 줌 (mm)'}
              size="small"
              value={defaultFocalLengthMm}
              onValueChange={(focal) => {
                setDefaultFocalLengthMm(focal);
                syncDefaultOpticsFromFocal(focal, resolutionW, resolutionH);
              }}
              min={1.8}
              max={16}
              emptyFallback={DEFAULT_FOCAL_LENGTH_MM}
              inputProps={{ min: 1.8, max: 16, step: 0.1 }}
              helperText={
                lensFixed
                  ? `권장 ${DEFAULT_FOCAL_LENGTH_MM} mm · H/V는 mm에서만 파생(잠금)`
                  : '줌 기준(H/V는 자동·조정 가능)'
              }
            />
          </FieldTip>
          {!lensFixed && (
            <>
              <FieldTip title="기본 수평 화각(°). 줌(초점)에서 자동 계산(읽기 전용).">
                <TextField
                  label="H-FOV ° (자동)"
                  type="number"
                  size="small"
                  value={defaultFovHDeg}
                  InputProps={{ readOnly: true }}
                  helperText="초점·해상도에서 파생"
                  sx={FOV_AUTO_INPUT_SX}
                />
              </FieldTip>
              <FieldTip title="기본 수직 화각(°). 줌(초점)에서 자동 계산(읽기 전용).">
                <TextField
                  label="V-FOV ° (자동)"
                  type="number"
                  size="small"
                  value={defaultFovVDeg}
                  InputProps={{ readOnly: true }}
                  helperText="초점·해상도에서 파생"
                  sx={FOV_AUTO_INPUT_SX}
                />
              </FieldTip>
            </>
          )}
          {lensFixed && (
            <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center', maxWidth: 280 }}>
              고정 {defaultFocalLengthMm} mm → H≈{Number(defaultFovHDeg).toFixed(1)}° / V≈
              {Number(defaultFovVDeg).toFixed(1)}° (참고·입력 아님)
            </Typography>
          )}
          <FieldTip title="센서 해상도. 같은 화각이면 해상도가 높을수록 픽셀당 선예도가 유리. 화각·초점 환산에도 쓰.">
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>해상도 (화각 계산)</InputLabel>
              <Select
                label="해상도 (화각 계산)"
                value={
                  RESOLUTION_PRESETS.find((p) => p.w === resolutionW && p.h === resolutionH)?.label ??
                  '1080p (1920×1080)'
                }
                onChange={(e) => {
                  const preset = RESOLUTION_PRESETS.find((p) => p.label === e.target.value);
                  if (preset) {
                    setResolutionW(preset.w);
                    setResolutionH(preset.h);
                    syncDefaultOpticsFromFocal(defaultFocalLengthMm, preset.w, preset.h);
                  }
                }}
              >
                {RESOLUTION_PRESETS.map((p) => (
                  <MenuItem key={p.label} value={p.label}>
                    {p.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </FieldTip>
        </Stack>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.25} flexWrap="wrap" useFlexGap sx={{ mt: 1.25 }}>
          <FieldTip title="OFF(기본): 벽면 따라 카메라 등간격(예: 3대면 셀 중앙 4.2·12.6·21.0m). ON: 모서리 사각을 줄이려 끝단을 조금 당김 — 끝 여유와 카메라 간격이 달라질 수 있음.">
            <FormControlLabel
              control={
                <Switch
                  checked={flexInstallSpacing}
                  onChange={(_, v) => setFlexInstallSpacing(v)}
                  size="small"
                />
              }
              label="카메라 간격 유동 조정"
            />
          </FieldTip>
          <Button
            variant="contained"
            onClick={() => applyAutoPlacement(poolPolygon, poolLengthM, poolWidthM)}
          >
            자동 배치
          </Button>
          <Button variant="outlined" onClick={() => applyWallLayoutPreset('1')}>
            1번 셋
          </Button>
          <Button variant="outlined" color="warning" onClick={() => applyWallLayoutPreset('baseline')}>
            2번 셋
          </Button>
        </Stack>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mt: 2 }}>
          {simulating && <Chip size="small" color="warning" label="계산 중…" />}
          {result && !simulating && (
            <>
              <Chip
                size="small"
                color="success"
                label={`커버리지 ${result.coverage_percent}% (${result.covered_area_m2 ?? '-'} m²)`}
              />
              <Chip size="small" label={`사각 ${result.blind_percent}%`} />
              <Chip
                size="small"
                color="success"
                variant="outlined"
                label={`겹침 영역 ${result.overlap_percent ?? 0}%`}
              />
            </>
          )}
        </Box>
      </Paper>

      <Stack direction="row" spacing={1} sx={{ mb: 1 }} flexWrap="wrap" alignItems="center" useFlexGap>
        <Typography variant="caption" color="text.secondary">
          초록 세기(겹친 CCTV 수):
        </Typography>
        {[1, 2, 3, 4].map((n) => (
          <Box
            key={n}
            sx={{
              width: 32,
              height: 16,
              borderRadius: 0.5,
              border: '1px solid #a5d6a7',
              bgcolor: `rgba(27, 94, 32, ${heatmapAlphaForStrength(n)})`,
            }}
            title={`${n}대`}
          />
        ))}
        <Typography variant="caption" color="text.secondary">
          연한 → 진한
        </Typography>
      </Stack>

      <Box
        ref={canvasHostRef}
        sx={{
          position: 'relative',
          width: '100%',
          overflowX: 'auto',
          overflowY: 'visible',
        }}
      >
      <canvas
        ref={canvasRef}
          width={canvasLayout.canvasW}
          height={canvasLayout.canvasH}
          style={{
            border: '1px solid #ccc',
            cursor: dragging ? 'grabbing' : addMode ? 'cell' : 'pointer',
            width: canvasLayout.canvasW,
            maxWidth: 'none',
            height: canvasLayout.canvasH,
            display: 'block',
            touchAction: 'none',
            background: '#fafafa',
          }}
          onMouseDown={onPointerDown}
          onMouseMove={onPointerMove}
          onMouseUp={onPointerUp}
          onMouseLeave={onPointerLeave}
        />
        <Box
          ref={hoverTipElRef}
          sx={{
            position: 'absolute',
            left: 8,
            right: 8,
            bottom: 8,
            zIndex: 2,
            pointerEvents: 'none',
            display: 'none',
            px: 1.25,
            py: 0.75,
            borderRadius: 1,
            bgcolor: 'rgba(13, 71, 161, 0.92)',
            color: '#fff',
            boxShadow: 2,
            fontSize: 12,
            lineHeight: 1.4,
          }}
        />
      </Box>

      <Stack direction="row" spacing={1} sx={{ mt: 2 }} flexWrap="wrap" useFlexGap alignItems="center">
        <ToggleButtonGroup
          exclusive
          size="small"
          value={addMount}
          onChange={(_, v) => v && setAddMount(v)}
        >
          <ToggleButton value="ceiling">추가: 천장</ToggleButton>
          <ToggleButton value="wall">추가: 벽</ToggleButton>
        </ToggleButtonGroup>
        <Button
          variant={addMode ? 'contained' : 'outlined'}
          color="secondary"
          onClick={() => {
            setAddMode((v) => !v);
            setMsg(
              !addMode
                ? `${addMount === 'wall' ? '벽' : '천장'} 카메라 추가 모드 — 도면을 클릭`
                : '추가 모드 해제',
            );
          }}
        >
          {addMode ? '추가 모드 ON' : '카메라 추가'}
        </Button>
        <Button variant="outlined" onClick={() => void loadLayout()} disabled={loading}>
          다시 불러오기
        </Button>
        <Button
          variant="outlined"
          onClick={() => void runSim(cameras, poolPolygon, poolLengthM, poolWidthM)}
          disabled={simulating || !cameras.length}
        >
          커버리지 다시 계산
        </Button>
        <Tooltip title="현재 위치·높이·벽·풀 거리로 팬과 틸트만 다시 계산. 위치·렌즈는 유지. 직접 입력한 팬·틸트는 유지.">
          <span>
            <Button variant="outlined" onClick={realignPanTilt} disabled={!cameras.length}>
              팬·틸트 다시 맞춤
            </Button>
          </span>
        </Tooltip>
        <Button
          variant="contained"
          color="success"
          onClick={() => void handleSave()}
          disabled={loading}
        >
          이 배치 저장
        </Button>
        <Button
          variant="outlined"
          onClick={handlePrintReport}
          disabled={!cameras.length || simulating}
        >
          결과 인쇄
        </Button>
        <Button
          variant="outlined"
          onClick={handleDownloadPng}
          disabled={!cameras.length || simulating}
        >
          도면 PNG
        </Button>
        <Button
          variant="outlined"
          onClick={handleDownloadJson}
          disabled={!cameras.length}
        >
          보고서 JSON
        </Button>
        <Button variant="outlined" color="warning" onClick={removeSelected} disabled={selected == null}>
          선택 삭제
        </Button>
      </Stack>

      <Divider sx={{ my: 2 }} />

      {cameras.length > 0 && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            자동 계산값 — CCTV별 틸트·팬·줌 (H/V는 줌 파생)
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
            행을 클릭하면 도면에서 해당 카메라가 강조됨. 벽 위 위치는{' '}
            <strong>왼쪽 끝 m</strong>(짧은 변은 <strong>위쪽 끝 m</strong>)로 입력.
            위치를 옮기면 팬이 그 앞을 보도록 다시 계산됨. 팬·틸트를 한꺼번에 맞출 때는 「팬·틸트 다시 맞춤」.
            직접 입력한 각도는 유지. 「커버리지 다시 계산」은 숫자·도면만 다시 받음.
            벽캠 팬(목록·화살표)은 카메라 기준: <strong>0°=정면</strong>, +우측(+10·+20…), −좌측(−10·−20…).
            윗·아랫벽 동일. 틸트는 설치 높이·풀 폭에 따라 자동 산출(고정 30° 아님). 권장{' '}
            {WALL_TILT_RECOMMENDED_MIN}~{WALL_TILT_RECOMMENDED_MAX}°.
          </Typography>
          <Stack spacing={1}>
            {cameras.map((cam, idx) => (
              <Stack
                key={`auto-${idx}`}
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1}
                alignItems={{ sm: 'center' }}
                sx={{
                  py: 0.5,
                  px: 1,
                  borderRadius: 1,
                  bgcolor: selected === idx ? 'action.selected' : 'grey.50',
                  border: selected === idx ? '1px solid' : '1px solid transparent',
                  borderColor: selected === idx ? 'warning.main' : 'transparent',
                  cursor: 'pointer',
                }}
                onMouseDown={(e) => {
                  // TextField 포커스 전에 선택 — 화각 즉시 강조
                  if ((e.target as HTMLElement).closest('input,textarea,button')) return;
                  selectCamera(idx);
                }}
                onClick={(e) => {
                  if ((e.target as HTMLElement).closest('input,textarea,button')) return;
                  selectCamera(idx);
                }}
              >
                <Typography variant="body2" sx={{ minWidth: 140 }}>
                  {idx + 1}. {cam.name || `CCTV ${idx + 1}`}
                </Typography>
                {(() => {
                  const bounds = poolBoundsFromPolygon(poolPolygon);
                  const side =
                    cam.mount_type === 'wall' ? inferWallSide(cam, bounds) : 'top';
                  const alongShort = side === 'left' || side === 'right';
                  const spanM = alongShort ? poolWidthM : poolLengthM;
                  const meters = Math.round(
                    alongEdgeMeters(cam, side, bounds, poolLengthM, poolWidthM) * 100,
                  ) / 100;
                  return (
                    <FieldTip
                      title={
                        alongShort
                          ? '위쪽 수면 끝에서 이 카메라까지(m). 짧은 변을 따라 이동. 팬은 그 앞을 보도록 다시 계산. 팬을 직접 입력하면 유지.'
                          : '왼쪽 수면 끝에서 이 카메라까지(m). 긴 변을 따라 이동. 예: 3.7. 팬은 그 앞을 보도록 다시 계산. 팬을 직접 입력하면 유지.'
                      }
                    >
                      <DraftNumberField
                        label={alongShort ? '위쪽 끝 m' : '왼쪽 끝 m'}
                        size="small"
                        value={meters}
                        onFocus={() => selectCamera(idx)}
                        onClick={(e) => e.stopPropagation()}
                        onValueChange={(v) => commitAlongMeters(idx, v)}
                        min={0}
                        max={spanM}
                        emptyFallback={meters}
                        inputProps={{ min: 0, max: spanM, step: 0.01 }}
                        sx={{ width: 118 }}
                      />
                    </FieldTip>
                  );
                })()}
                <FieldTip
                  title={`틸트(°). 고정 30°가 아님 — 높이·풀 폭·맞은편 벽캠에 따라 자동 산출. 권장 ${WALL_TILT_RECOMMENDED_MIN}~${WALL_TILT_RECOMMENDED_MAX}°, 입력 상한 38°. 45° 이상은 탑뷰에 가까워 불리.`}
                >
                  <DraftNumberField
                    label="틸트 °"
                    size="small"
                    value={cam.tilt_deg}
                    onFocus={() => selectCamera(idx)}
                    onValueChange={(v) => updateCamDerivedField(idx, 'tilt_deg', v)}
                    min={isWallCameraSpec(cam) ? 15 : 5}
                    max={isWallCameraSpec(cam) ? 38 : 90}
                    emptyFallback={cam.tilt_deg}
                    helperText={
                      isWallCameraSpec(cam)
                        ? `권장 ${WALL_TILT_RECOMMENDED_MIN}~${WALL_TILT_RECOMMENDED_MAX}°`
                        : undefined
                    }
                    FormHelperTextProps={{ sx: { mx: 0, whiteSpace: 'nowrap' } }}
                    inputProps={
                      isWallCameraSpec(cam)
                        ? { min: 15, max: 38, step: 0.1 }
                        : { min: 5, max: 90, step: 0.1 }
                    }
                    sx={{ width: 100 }}
                  />
                </FieldTip>
                {(() => {
                  const bounds = poolBoundsFromPolygon(poolPolygon);
                  const isWall = isWallCameraSpec(cam);
                  if (isWall) {
                    const side = inferWallSide(cam, bounds);
                    const face = wallFaceHeadingDeg(side);
                    const disp = absoluteToCameraPanDisplayDeg(
                      Number(cam.heading_deg),
                      face,
                    );
                    return (
                      <FieldTip title="팬(°). 카메라가 보는 방향 기준. 0=정면(수면 안쪽), +10·+20=우측, −10·−20=좌측. 윗·아랫벽 동일.">
                        <DraftNumberField
                          label="팬 °(캠)"
                          size="small"
                          value={disp}
                          onFocus={() => selectCamera(idx)}
                          onClick={(e) => e.stopPropagation()}
                          onValueChange={(v) =>
                            updateCamDerivedField(
                              idx,
                              'heading_deg',
                              cameraPanDisplayToAbsoluteDeg(v, face),
                            )
                          }
                          min={-180}
                          max={180}
                          emptyFallback={disp}
                          inputProps={{ min: -180, max: 180, step: 0.1 }}
                          helperText="0=정면 · +=우 · −=좌"
                          FormHelperTextProps={{ sx: { mx: 0, whiteSpace: 'nowrap' } }}
                          sx={{ width: 128 }}
                        />
                      </FieldTip>
                    );
                  }
                  return (
                    <FieldTip title="팬(°). 천장캠은 도면 절대각. 0=우측, 90=아래, 180=좌, 270=위.">
                      <DraftNumberField
                        label="팬 °(도면)"
                        size="small"
                        value={cam.heading_deg}
                        onFocus={() => selectCamera(idx)}
                        onValueChange={(v) => updateCamDerivedField(idx, 'heading_deg', v)}
                        min={0}
                        max={359}
                        emptyFallback={cam.heading_deg}
                        inputProps={{ min: 0, max: 359, step: 0.1 }}
                        helperText="도면 0=우측 · 90=아래"
                        FormHelperTextProps={{ sx: { mx: 0, whiteSpace: 'nowrap' } }}
                        sx={{ width: 118 }}
                      />
                    </FieldTip>
                  );
                })()}
                <FieldTip
                  title={
                    lensFixed
                      ? '고정 렌즈: 상단 「고정 초점(mm)」만 사용. 카메라별 줌 변경 불가.'
                      : '줌(초점 mm). 올리면 화각이 좁아지고, 내리면 넓게 봄. H/V는 줌에 맞춰 자동.'
                  }
                >
                  <DraftNumberField
                    label={lensFixed ? '초점 mm' : '줌 mm'}
                    size="small"
                    value={cam.focal_length_mm ?? defaultFocalLengthMm}
                    onFocus={() => selectCamera(idx)}
                    onValueChange={(v) => updateCamDerivedField(idx, 'focal_length_mm', v)}
                    min={1.8}
                    max={16}
                    emptyFallback={defaultFocalLengthMm}
                    inputProps={{ min: 1.8, max: 16, step: 0.1, readOnly: lensFixed }}
                    InputProps={{ readOnly: lensFixed }}
                    sx={{ width: 100 }}
                  />
                </FieldTip>
                {!lensFixed && (
                  <>
                    <FieldTip title="수평 화각(°). 줌(초점)에서 자동 계산(읽기 전용).">
                      <TextField
                        label="H-FOV °"
                        size="small"
                        type="number"
                        value={cam.fov_h_deg ?? defaultFovHDeg}
                        onFocus={() => selectCamera(idx)}
                        onClick={(e) => e.stopPropagation()}
                        InputProps={{ readOnly: true }}
                        sx={{ width: 108 }}
                      />
                    </FieldTip>
                    <FieldTip title="수직 화각(°). 줌(초점)에서 자동 계산(읽기 전용).">
                      <TextField
                        label="V-FOV °"
                        size="small"
                        type="number"
                        value={cam.fov_v_deg ?? defaultFovVDeg}
                        onFocus={() => selectCamera(idx)}
                        onClick={(e) => e.stopPropagation()}
                        InputProps={{ readOnly: true }}
                        sx={{ width: 108 }}
                      />
                    </FieldTip>
                  </>
                )}
                {lensFixed && (
                  <Typography variant="caption" color="text.secondary" sx={{ minWidth: 140 }}>
                    H/V 잠금 (mm 파생)
                  </Typography>
                )}
              </Stack>
            ))}
          </Stack>
        </Paper>
      )}

      {msg && (
        <Alert severity="success" sx={{ mt: 2 }} onClose={() => setMsg(null)}>
          {msg}
        </Alert>
      )}
      {err && (
        <Alert severity="error" sx={{ mt: 2 }} onClose={() => setErr(null)}>
          {err}
        </Alert>
      )}
    </Paper>
  );
};

export default CameraLayoutSimulator;
